/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import i18n from "./i18n/i18n.js";
import SecurityService from "./auth/SecurityService.js";
import AuthService from "./auth/authService";

var $t = (args) => i18n.t(args);
Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "/",
          redirect: "/dashboard/analytics",
        },
        {
          path: "/dashboard/analytics",
          name: "dashboardAnalytics",
          component: () => import("./views/Dashboard/DashboardAnalytics.vue"),
          meta: {
            rule: "editor",
            permission: "PrimaryAccess",
          },
        },
        {
          path: "/dashboard/analytics2",
          name: "dashboardAnalytics2",
          component: () => import("./views/Dashboard/DashboardECommerce.vue"),
          meta: {
            rule: "editor",
            permission: "PrimaryAccess",
          },
        },
        // =============================================================================
        //usersManagement
        {
          path: "/Users/Index",
          name: $t("Users"),
          component: () => import("@/views/user-management/Users/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت کاربران" },
              { title: $t("Users"), active: true },
            ],
            pageTitle: $t("Users"),
            rule: "editor",
            permission: "GetAllUser|GetAllUserByUserId",
          },
        },
        {
          path: "/Roles/Index",
          name: $t("Roles"),
          component: () => import("@/views/user-management/Roles/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت کاربران" },
              { title: $t("Roles"), active: true },
            ],
            pageTitle: $t("Roles"),
            rule: "editor",
            permission:
              "GetUsersByRoleId|GetAllPermissionsByRoleId|DeleteRole|CreateRole|ModifyRole",
          },
        },
        {
          path: "/Permissions/Index",
          name: $t("Roles"),
          component: () =>
            import("@/views/user-management/Permissions/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت کاربران" },
              { title: $t("Permissions"), active: true },
            ],
            pageTitle: $t("Permissions"),
            rule: "editor",
            permission: "CreatePermission|ModifyPermission|DeletePermission",
          },
        },
        {
          path: "/SubmitNewUser/Index",
          name: $t("Roles"),
          component: () =>
            import("@/views/user-management/SubmitNewUser/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت کاربران" },
              { title: $t("SubmitNewUser"), active: true },
            ],
            pageTitle: $t("SubmitNewUser"),
            rule: "editor",
            permission: "CreateUser",
          },
        },
        //warehouseManagement

        {
          path: "/WarehouseProducts/Index",
          name: $t("Products"),
          component: () =>
            import("@/views/warehouse-management/Products/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("Products"), active: true },
            ],
            pageTitle: $t("Products"),
            rule: "editor",
            permission: "GetAllStock",
          },
        },
        {
          path: "/WarehouseProducts/AllProducts",
          name: $t("AllProducts"),
          component: () =>
            import("@/views/warehouse-management/Products/All.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("Products"),
                url: "WarehouseProducts/Index",
              },
              { title: $t("AllProducts"), active: true },
            ],
            pageTitle: $t("AllProducts"),
            rule: "editor",
          },
        },
        {
          path: "/WarehouseProducts/WaitingStatus",
          name: $t("WaitingForStatusDetermination"),
          component: () =>
            import("@/views/warehouse-management/Products/WaitingStatus.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("Products"),
                url: "WarehouseProducts/Index",
              },
              { title: $t("WaitingForStatusDetermination"), active: true },
            ],
            pageTitle: $t("WaitingForStatusDetermination"),
            rule: "editor",
          },
        },
        {
          path: "/warehouse/Index",
          name: $t("Warehouses"),
          component: () =>
            import("@/views/warehouse-management/Warehouses/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("Warehouses"), active: true },
            ],
            pageTitle: $t("Warehouses"),
            rule: "editor",
            permission:
              "CreateWarehouse|DeleteWarehouse|ModifyWarehouse|GetAllWarehouse|AddShelve|ModifyShelve|RemoveShelve",
          },
        },
        {
          path: "/CheckInTransactions/Index",
          name: $t("CheckInTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckInTransactions/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("CheckInTransactions"), active: true },
            ],
            pageTitle: $t("CheckInTransactions"),
            rule: "editor",
            permission:
              "CheckInWithExcelFile|RegisterCheckIn|GetCheckIns|GetInventoryTransactionItemsByInventoryTransaction|GetInventoryTransactionHeader|GetSerialsOfStock",
          },
        },
        {
          path: "/CheckInTransactions/Create",
          name: $t("CreateCheckInTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckInTransactions/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckInTransactions"),
                url: "/CheckInTransactions/Index",
              },
              { title: $t("CreateCheckInTransactions"), active: true },
            ],
            pageTitle: $t("CreateCheckInTransactions"),
            rule: "editor",
          },
        },
        {
          path: "/CheckInTransactions/CreateCheckInForReturnCheckOut",
          name: $t("CreateCheckInForReturnCheckOut"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckInTransactions/CreateCheckInForReturnCheckOut.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckInTransactions"),
                url: "/CheckInTransactions/Index",
              },
              { title: $t("CreateCheckInForReturnCheckOut"), active: true },
            ],
            pageTitle: $t("CreateCheckInForReturnCheckOut"),
            rule: "editor",
          },
        },
        {
          path: "/CheckInTransactions/Details/:id",
          name: $t("CheckInDetails"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckInTransactions/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckInTransactions"),
                url: "/CheckInTransactions/Index",
              },
              { title: $t("CheckInDetails"), active: true },
            ],
            pageTitle: $t("CheckInDetails"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutTransactions/Details/:id",
          name: $t("CheckOutDetails"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("CheckOutDetails"), active: true },
            ],
            pageTitle: $t("CheckOutDetails"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutForDraftOrder/Details/:id",
          name: $t("CheckOutDetails"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/CheckOutForDraftOrderDetails.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("CheckOutDetails"), active: true },
            ],
            pageTitle: $t("CheckOutDetails"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutForDraftOrder/PrintWarrantyCard",
          name: $t("PrintWarrantyCard"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/PrintWarrantyCard.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("PrintWarrantyCard"), active: true },
            ],
            pageTitle: $t("PrintWarrantyCard"),
            rule: "editor",
          },
        },
        {
          path: "/DischargeCheckOut/PrintLabel2",
          name: $t("PrintLabel"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/PrintLabel2.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermissions"),
                url: "/CheckOutTransactions/Index",
              },
              {
                title: $t("DischargeCheckOut"),
                url: "/DischargePermissions/DischargeCheckOut/:id",
              },
              { title: $t("PrintLabel"), active: true },
            ],
            pageTitle: $t("PrintLabel"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutForDraftOrder/PrintWarrantyCard2",
          name: $t("PrintWarrantyCard"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/PrintWarrantyCard2.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("PrintWarrantyCard"), active: true },
            ],
            pageTitle: $t("PrintWarrantyCard"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutForDraftOrder/PrintLabel",
          name: $t("PrintLabel"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/PrintLabel.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("PrintLabel"), active: true },
            ],
            pageTitle: $t("PrintLabel"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutInvoice/:id",
          name: $t("CheckOutInvoice"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/CheckOutInvoice.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("Invoice"), active: true },
            ],
            pageTitle: $t("Invoice"),
            rule: "editor",
          },
        },
        {
          path: "/CheckOutTransactions/Index",
          name: $t("CheckOutTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("CheckOutTransactions"), active: true },
            ],
            pageTitle: $t("CheckOutTransactions"),
            rule: "editor",
            permission:
              "GetInventoryTransactionHeader|CheckOutWithExcelFile|RegisterCheckOut|GetInventoryTransactionItemsByInventoryTransaction|GetCheckOutForDraftOrderTransactionById|GetStockDraftOrderForPrint|CreateOrder|GetDischargePermissionByIdAndStoreId|GetRecipientInfoBydraftOrderId|GetPackagesInfo",
          },
        },
        {
          path: "/CheckOutTransactions/Create",
          name: $t("CreateCheckOutTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("CheckOutTransactions"),
                url: "/CheckOutTransactions/Index",
              },
              { title: $t("CreateCheckOutTransactions"), active: true },
            ],
            pageTitle: $t("CreateCheckOutTransactions"),
            rule: "editor",
          },
        },
        {
          path: "/DischargePermissions/Index",
          name: $t("DischargePermissions"),
          component: () =>
            import(
              "@/views/warehouse-management/DischargePermissions/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("DischargePermissions"), active: true },
            ],
            pageTitle: $t("DischargePermissions"),
            rule: "editor",
            permission:
              "GetDischargePermissionByIdAndStoreId|ChangeStateToReject|ChangeStateToConfirm|GetRecipientInfoBydraftOrderId|GetRecipientInfoBydraftOrderId|GetStockDraftOrderById|CreateDischargePermission|RegisterCheckOutForDraftOrder",
          },
        },
        {
          path: "/DischargePermissions/DischargeCheckOut/:id",
          name: $t("DischargeCheckOut"),
          component: () =>
            import(
              "@/views/warehouse-management/CheckOutTransactions/DischargeCheckOut.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermissions"),
                url: "/DischargePermissions/Index",
              },
              { title: $t("DischargeCheckOut"), active: true },
            ],
            pageTitle: $t("DischargeCheckOut"),
            rule: "editor",
          },
        },
        {
          path: "/DischargePermissions/Details/:tabIndex/:id",
          name: $t("DischargePermission"),
          component: () =>
            import(
              "@/views/warehouse-management/DischargePermissions/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermission"),
                url: "/DischargePermissions/Index",
              },
              { title: $t("DischargePermission"), active: true },
            ],
            pageTitle: $t("DischargePermission"),
            rule: "editor",
          },
        },
        {
          path: "/DischargePermissions/Label/:id",
          name: $t("Label"),
          component: () =>
            import(
              "@/views/warehouse-management/DischargePermissions/Label.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermission"),
                url: "/DischargePermissions/Index",
              },
              { title: $t("DischargePermission"), active: true },
            ],
            pageTitle: $t("Label"),
            rule: "editor",
          },
        },
        {
          path: "/DischargePermissions/Create",
          name: $t("CreateDischargePermission"),
          component: () =>
            import(
              "@/views/warehouse-management/DischargePermissions/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermission"),
                url: "/DischargePermissions/Index",
              },
              { title: $t("CreateDischargePermission"), active: true },
            ],
            pageTitle: $t("CreateDischargePermission"),
            rule: "editor",
          },
        },
        {
          path: "/ShowPackageInfo/:id/:stockDraftOrderId",
          name: $t("ShowPackageInfo"),
          component: () =>
            import("@/views/warehouse-management/ShowPackageInfo/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("DischargePermission"),
                url: "/DischargePermissions/Index",
              },
              { title: $t("ShowPackageInfo"), active: true },
            ],
            pageTitle: $t("ShowPackageInfo"),
            rule: "editor",
          },
        },
        {
          path: "/TransferTransactions/Index",
          name: $t("TransferTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/TransferTransactions/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("TransferTransactions"), active: true },
            ],
            pageTitle: $t("TransferTransactions"),
            rule: "editor",
            permission:
              "RegisterTransferByExcelFile|RegisterTransfer|GetTransferItems|GetTransferHeader",
          },
        },
        {
          path: "/TransferTransactions/Create",
          name: $t("CreateTransferTransactions"),
          component: () =>
            import(
              "@/views/warehouse-management/TransferTransactions/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("TransferTransactions"),
                url: "/TransferTransactions/Index",
              },
              { title: $t("CreateTransferTransactions"), active: true },
            ],
            pageTitle: $t("CreateTransferTransactions"),
            rule: "editor",
          },
        },
        {
          path: "/TransferTransactions/Details/:id",
          name: $t("TransferTransactionsDetails"),
          component: () =>
            import(
              "@/views/warehouse-management/TransferTransactions/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("TransferTransactions"),
                url: "/TransferTransactions/Index",
              },
              { title: $t("TransferTransactionsDetails"), active: true },
            ],
            pageTitle: $t("TransferTransactionsDetails"),
            rule: "editor",
          },
        },
        {
          path: "/Cardex/Index",
          name: $t("Cardex"),
          component: () =>
            import("@/views/warehouse-management/Cardex/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("Cardex"), active: true },
            ],
            pageTitle: $t("Cardex"),
            rule: "editor",
            permission: "GetKardexTransactionOfStocks",
          },
        },
        {
          path: "/Balance/Index",
          name: $t("StockBalance"),
          component: () =>
            import("@/views/warehouse-management/Balance/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("StockBalance"), active: true },
            ],
            pageTitle: $t("StockBalance"),
            rule: "editor",
            permission:
              "GetAllInventoryBalances|GetAllInventoriesStocksBalances",
          },
        },
        {
          path: "/StockCirculation/Index",
          name: $t("StockCirculationBalance"),
          component: () =>
            import(
              "@/views/warehouse-management/Reports/StockCirculation/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("StockCirculation"), active: true },
            ],
            pageTitle: $t("StockCirculation"),
            rule: "editor",
          },
        },
        {
          path: "/SerialCirculation/Index",
          name: $t("SerialCirculation"),
          component: () =>
            import(
              "@/views/warehouse-management/Reports/SerialCirculation/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("SerialCirculation"), active: true },
            ],
            pageTitle: $t("SerialCirculation"),
            rule: "editor",
          },
        },
        {
          path: "/SerialBalance/Index",
          name: $t("SerialBalance"),
          component: () =>
            import(
              "@/views/warehouse-management/Reports/SerialBalance/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("SerialBalance"), active: true },
            ],
            pageTitle: $t("SerialBalance"),
            rule: "editor",
          },
        },
        {
          path: "/InventoryTransactionOperation/Index",
          name: $t("InventoryTransactionOperation"),
          component: () =>
            import(
              "@/views/warehouse-management/InventoryTransactionOperation/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("InventoryTransactionOperation"), active: true },
            ],
            pageTitle: $t("InventoryTransactionOperation"),
            rule: "editor",
            permission: "GetInventoryTransactionItemsByInventoryTransaction",
          },
        },
        {
          path: "/InventoryTransactionOperation/Items/:id/0",
          name: $t("Items"),
          component: () =>
            import(
              "@/views/warehouse-management/InventoryTransactionOperation/Items.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("InventoryTransactionOperation"),
                url: "/InventoryTransactionOperation/Index",
              },
              { title: $t("Items"), active: true },
            ],
            pageTitle: $t("Items"),
            rule: "editor",
          },
        },
        {
          path: "/InventoryReservation/Index",
          name: $t("Reports"),
          component: () =>
            import(
              "@/views/warehouse-management/InventoryReservation/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("InventoryReservation"), active: true },
            ],
            pageTitle: $t("InventoryReservation"),
            rule: "editor",
            permission: "CreateReserve|CreateRecharge",
          },
        },
        {
          path: "/InventoryReservations/Index",
          name: $t("Reports"),
          component: () =>
            import(
              "@/views/warehouse-management/InventoryReservations/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("InventoryReservations"), active: true },
            ],
            pageTitle: $t("InventoryReservations"),
            rule: "editor",
            permission: "GetAllReservation",
          },
        },
        {
          path: "/Reports/Index",
          name: $t("Reports"),
          component: () =>
            import("@/views/warehouse-management/Reports/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("Reports"), active: true },
            ],
            pageTitle: $t("Reports"),
            rule: "editor",
            permission: "GetSerialsOfStock",
          },
        },
        {
          path: "/WarehouseHandling/Index",
          name: $t("Reports"),
          component: () =>
            import("@/views/warehouse-management/WarehouseHandling/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              { title: $t("WarehouseHandling"), active: true },
            ],
            pageTitle: $t("WarehouseHandling"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/WarehouseHandling/Details/:id",
          name: $t("ListOfWarehouseHandlingsThisTerm"),
          component: () =>
            import(
              "@/views/warehouse-management/WarehouseHandling/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("WarehouseHandling"),
                url: "/WarehouseHandling/Index",
              },
              { title: $t("ListOfWarehouseHandlingsThisTerm"), active: true },
            ],
            pageTitle: $t("ListOfWarehouseHandlingsThisTerm"),
            rule: "editor",
          },
        },
        {
          path: "/WarehouseHandling/CountingTag/Details/:id",
          name: $t("DetailsCounting"),
          component: () =>
            import(
              "@/views/warehouse-management/WarehouseHandling/CountingTag/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("WarehouseHandling"),
                url: "/WarehouseHandling/Index",
              },
              {
                title: $t("ListOfWarehouseHandlingsThisTerm"),
                url: "/WarehouseHandling/Details/",
              },
              { title: $t("DetailsCounting"), active: true },
            ],
            pageTitle: $t("ListOfWarehouseHandlingsThisTerm"),
            rule: "editor",
          },
        },
        // {
        //   path:
        //     "/WarehouseHandling/GroupingAndStartingWarehouseHanding/Details/:id",
        //   name: $t("DetailsGroupingAndStartingWarehouseHanding"),
        //   component: () =>
        //     import("@/views/warehouse-management/WarehouseHandling/GroupingAndStartingWarehouseHanding/Details.vue"),
        //   meta: {
        //     breadcrumb: [
        //       { title: "Home", url: "/" },
        //       { title: $t("WarehouseManagement") },
        //       {
        //         title: $t("WarehouseHandling"),
        //         url: "/WarehouseHandling/Index"
        //       },
        //       { title: $t("ListOfWarehouseHandlingsThisTerm") },
        //       { title: $t("DetailsCounting"), active: true },
        //       { title: $t("GroupingAndStartingWarehouseHanding"), active: true }
        //     ],
        //     pageTitle: $t("GroupingAndStartingWarehouseHanding"),
        //     rule: "editor"
        //   }
        // },

        {
          path: "/TransferTransactions/Details/:id",
          name: $t("TransferTransactionsDetails"),
          component: () =>
            import(
              "@/views/warehouse-management/TransferTransactions/Details.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("WarehouseManagement") },
              {
                title: $t("TransferTransactions"),
                url: "/TransferTransactions/Index",
              },
              { title: $t("TransferTransactionsDetails"), active: true },
            ],
            pageTitle: $t("TransferTransactionsDetails"),
            rule: "editor",
          },
        },
        // {
        //   path: "/MomentaryInventory/Index",
        //   name: $t("MomentaryInventory"),
        //   component: () =>
        //     import("@/views/warehouse-management/MomentaryInventory/Index.vue"),
        //   meta: {
        //     breadcrumb: [
        //       { title: "Home", url: "/" },
        //       { title: $t("WarehouseManagement") },
        //       { title: $t("MomentaryInventory"), active: true }
        //     ],
        //     pageTitle: $t("MomentaryInventory"),
        //     rule: "editor"
        //     // permission: "ProductManagement_SpecificProduct_View_List"
        //   }
        // },
        //productManagement
        {
          path: "/SpecificProduct/List",
          name: $t("Items"),
          component: () =>
            import("@/views/products-management/SpecificProduct/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Items"), active: true },
            ],
            pageTitle: $t("Items"),
            rule: "editor",
            permission:
              "CreateSpecificProduct|GetAllSpecificProductsByExcelFIle|CreateSpecificProductByExcelFile|DeleteSpecificProduct|ModifySpecificProduct",
          },
        },
        {
          path: "/ActualProduct/List",
          name: $t("Products"),
          component: () => import("@/views/products-management/List.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Products"), active: true },
            ],
            pageTitle: $t("Products"),
            rule: "editor",
            permission:
              "GetActualProductbyId|ModifyActualProduct|ModifyProductAttribute|GetPictureByActualProduct|AttachFileActualProduct|UnAttachFileActualProduct|ModifyPictureActualProduct|GetAllActualProductsExcelFile|CreateActualProduct|DeleteActualProduct|CreateActualProductByExcelFile|GetVideoByActualProduct",
          },
        },
        {
          path: "/ActualProduct/Create",
          name: $t("CreateNewProduct"),
          component: () => import("@/views/products-management/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Products"), url: "/ActualProduct/List" },
              { title: $t("CreateNewProduct"), active: true },
            ],
            pageTitle: $t("CreateNewProduct"),
            rule: "editor",
          },
        },
        {
          path: "/ActualProduct/Edit/:id",
          name: $t("EditProduct"),
          component: () => import("@/views/products-management/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Products"), url: "/ActualProduct/List" },
              { title: $t("EditProduct"), active: true },
            ],
            pageTitle: $t("EditProduct"),
            rule: "editor",
          },
        },
        {
          path: "/GenericProduct/Index",
          name: $t("GenericProducts"),
          component: () =>
            import("@/views/products-management/GenericProduct/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("GenericProducts"), active: true },
            ],
            pageTitle: $t("GenericProducts"),
            rule: "editor",
            permission:
              "UnAssignGenericProductItem|AssignGenericProductItem|DeleteGenericProduct|CreateGenericProduct|GetAllGenericProduct|GetGenericProductById|DeleteAttribute|ModifyAttribute|GetAllAttribute",
          },
        },
        {
          path: "/Categories/Index",
          name: $t("Categories"),
          component: () =>
            import("@/views/products-management/Categories/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Categories"), active: true },
            ],
            pageTitle: $t("Categories"),
            rule: "editor",
            permission:
              "CreatePromotionCategory|ModifyPromotionCategory|DeletePromotionCategory",
          },
        },
        {
          path: "/GenericProduct/Edit/:id",
          name: $t("Attributes"),
          component: () =>
            import("@/views/products-management/GenericProduct/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("GenericProducts"), url: "/GenericProduct/Index" },
              { title: $t("Attributes"), active: true },
            ],
            pageTitle: $t("Attributes"),
            rule: "editor",
          },
        },
        {
          path: "/Colors/Index",
          name: $t("Colors"),
          component: () =>
            import("@/views/products-management/Colors/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Colors"), active: true },
            ],
            pageTitle: $t("Colors"),
            rule: "editor",
            permission:
              "CreateProductColor|ModifyProductColor|DeleteProductColor",
          },
        },
        {
          path: "/Brands/Index",
          name: $t("Brands"),
          component: () =>
            import("@/views/products-management/Brands/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Brands"), active: true },
            ],
            pageTitle: $t("Brands"),
            rule: "editor",
            permission: "DeleteBrand|ModifyBrand|CreateBrand",
          },
        },
        {
          path: "/Adjectives/Index",
          name: $t("Adjectives"),
          component: () =>
            import("@/views/products-management/Adjectives/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Adjectives"), active: true },
            ],
            pageTitle: $t("Adjectives"),
            rule: "editor",
            permission: "DeleteBrand|ModifyBrand|CreateBrand",
          },
        },
        {
          path: "/TermsOfWarrantyProducts/Index",
          name: $t("TermsOfWarrantyProducts"),
          component: () =>
            import(
              "@/views/products-management/TermsOfWarrantyProducts/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("TermsOfWarrantyProducts"), active: true },
            ],
            pageTitle: $t("TermsOfWarrantyProducts"),
            rule: "editor",
            permission: "GetAllProductsKhadamatWarrantyByKeyword",
          },
        },
        {
          path: "/Warranties/Index",
          name: $t("Warranties"),
          component: () =>
            import("@/views/products-management/Warranties/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("Warranties"), active: true },
            ],
            pageTitle: $t("Warranties"),
            rule: "editor",
            permission: "CreateWarranty|ModifyWarranty|DeleteWarranty",
          },
        },

        {
          path: "/ConfigurableProducts/List",
          name: $t("ConfigurableProducts"),
          component: () =>
            import("@/views/products-management/ConfigurableProducts/List.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              { title: $t("ConfigurableProducts"), active: true },
            ],
            pageTitle: $t("ConfigurableProducts"),
            rule: "editor",
            permission:
              "ModifyPictureActualProduct|UnAttachFileActualProduct|AttachFileActualProduct|GetPictureByActualProduct|UpdateConfigurableProduct|DeleteConfigurableProduct|CreateConfigurableProduct|GetConfigurableProductById|GetAllGenericProduct||",
          },
        },
        {
          path: "/ConfigurableProducts/Create",
          name: $t("CreateNewConfigurableProduct"),
          component: () =>
            import(
              "@/views/products-management/ConfigurableProducts/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              {
                title: $t("ConfigurableProducts"),
                url: "/ConfigurableProducts/List",
              },
              { title: $t("CreateNewConfigurableProduct"), active: true },
            ],
            pageTitle: $t("CreateNewConfigurableProduct"),
            rule: "editor",
          },
        },
        {
          path: "/ConfigurableProducts/Edit/:id",
          name: $t("EditConfigurableProducts"),
          component: () =>
            import("@/views/products-management/ConfigurableProducts/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ProductManagement") },
              {
                title: $t("ConfigurableProducts"),
                url: "/ConfigurableProducts/List",
              },
              { title: $t("EditConfigurableProduct"), active: true },
            ],
            pageTitle: $t("EditConfigurableProduct"),
            rule: "editor",
          },
        },
        //procurement
                {
                  path: "/ProcurementManagement/PurchaseRequest",
                  name: $t("PurchaseRequest"),
                  component: () => import("@/views/ProcurementManagement/PurchaseRequests/PurchaseRequest.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseRequest"), active: true },
                    ],
                    pageTitle: $t("PurchaseRequest"),
                    rule: "editor",
                    permission: "CreatePurchaseRequest|GetAllGetPurchaseRequestsByPaging|GetPurchaseRequestById",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseRequest/Details/:id",
                  name: $t("PurchaseRequestDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseRequests/Details.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseRequest"), url: 'ProcurementManagement/PurchaseRequest' },
                      { title: $t("PurchaseRequestDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseRequestDetails"),
                    rule: "editor",
                    permission: "GetPurchaseRequestById|ConfirmPurchaseRequest|RejectPurchaseRequest|ModifyPurchaseRequest",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseRequest/ShowDetails/:id",
                  name: $t("PurchaseRequestDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseRequests/ShowDetails.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseRequest"), url: '/ProcurementManagement/PurchaseRequest' },
                      { title: $t("PurchaseRequestDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseRequestDetails"),
                    rule: "editor",
                    permission: "GetPurchaseRequestById",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseRequest/:id",
                  name: $t("PurchaseRequestDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseRequests/EditPurchaseRequest.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseRequest"), url: '/ProcurementManagement/PurchaseRequest' },
                      { title: $t("PurchaseRequestDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseRequestDetails"),
                    rule: "editor",
                    permission: "GetPurchaseRequestById|ClosePurchaseRequest|RejectPurchaseRequest|ModifyPurchaseRequest",
                  },
                },
                {
                  path: "/ProcurementManagement/CreatePurchaseRequest",
                  name: $t("PurchaseRequest"),
                  component: () => import("@/views/ProcurementManagement/PurchaseRequests/CreatePurchaseRequest.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseRequest"), url: '/ProcurementManagement/PurchaseRequest' },
                      { title: $t("CreatePurchaseRequest"), active: true },
                    ],
                    pageTitle: $t("PurchaseRequest"),
                    rule: "editor",
                    permission: "CreatePurchaseRequest",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseOrder",
                  name: $t("PurchaseOrder"),
                  component: () => import("@/views/ProcurementManagement/PurchaseOrder/PurchaseOrder.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseOrder"), active: true },
                    ],
                    pageTitle: $t("PurchaseOrder"),
                    rule: "editor",
                    permission: "GotoConfirm|GotoReject|GotoClosePurchaseOrder|GotoConfirmPurchaseOrder|SetSupplierInfoPurchaseOrder|GotoRejectPurchaseOrder|GetAllPurchaseOrdersByPaging|UpdateItemCodeOfPurchaseOrderItem|SetCurrencyType|UpdateUnitPriceOfPurchaseOrderItem|UpdateDeductionAndIncreasesOfPurchaseOrder|GetPurchaseOrderById|CreatePurchaseInvoice",                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseOrder/:id",
                  name: $t("PurchaseOrderDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseOrder/EditPurchaseOrder.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseOrder"), url: '/ProcurementManagement/PurchaseOrder' },
                      { title: $t("PurchaseOrderDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseOrderDetails"),
                    rule: "editor",
                    permission: "CreatePurchaseInvoice|GetPurchaseOrderById|GotoClosePurchaseOrder|GotoRejectPurchaseOrder|SetSupplierInfoPurchaseOrder|UpdateUnitPriceOfPurchaseOrderItem|UpdateDeductionAndIncreasesOfPurchaseOrder|GotoConfirm|GotoReject",                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseOrder/ShowDetails/:id",
                  name: $t("PurchaseOrderDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseOrder/ShowDetails.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseOrder"), url: '/ProcurementManagement/PurchaseOrder' },
                      { title: $t("PurchaseOrderDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseOrderDetails"),
                    rule: "editor",
                    permission: "GetPurchaseOrderById",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseOrder/DeductionAndIncrease/:id",
                  name: $t("DeductionAndIncrease"),
                  component: () => import("@/views/ProcurementManagement/DeductionAndIncrease/PurchaseOrderDeductionAndIncrease.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseOrder"), url: '/ProcurementManagement/PurchaseOrder' },
                      { title: $t("PurchaseOrderDetails") },
                      { title: $t("DeductionAndIncrease"), active: true },
                    ],
                    pageTitle: $t("DeductionAndIncrease"),
                    rule: "editor",
                    permission: "UpdateDeductionAndIncreasesOfPurchaseOrder",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseOrder/Details/:id",
                  name: $t("PurchaseOrderDetails"),
                  component: () => import("@/views/ProcurementManagement/PurchaseOrder/Details.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PurchaseOrder"), url: '/ProcurementManagement/PurchaseOrder' },
                      { title: $t("PurchaseOrderDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseOrderDetails"),
                    rule: "editor",
                    permission: "GetPurchaseOrderById|GotoConfirmPurchaseOrder",
                  },
                },
                {
                  path: "/ProcurementManagement/PreInvoice",
                  name: $t("PreInvoice"),
                  component: () => import("@/views/ProcurementManagement/PreInvoice/PreInvoice.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), active: true },
                    ],
                    pageTitle: $t("PreInvoice"),
                    rule: "editor",
                    permission: "PurchaseInvoice|GotoConfirm|GotoReject|GetPurchaseInvoiceById|GetAllPurchaseInvoicesByPaging|GotoClose|UpdateCurrencyInfoCommand|UpdateDeductionAndIncreasesOfPurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseInvoice/:id",
                  name: $t("PurchaseInvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/PreInvoice/Details.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), url:'/ProcurementManagement/PreInvoice' },
                      { title: $t("PurchaseInvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseInvoiceDetails"),
                    rule: "editor",
                    permission: "GotoReject|GotoClose|GetPurchaseInvoiceById|UpdateDeductionAndIncreasesOfPurchaseInvoice|UpdateCurrencyInfoCommand|PurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseInvoice/RemainingPurchaseOrder/:id",
                  name: $t("PurchaseInvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/PreInvoice/EditPreInvoice.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), url:'/ProcurementManagement/PreInvoice' },
                      { title: $t("PurchaseInvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseInvoiceDetails"),
                    rule: "editor",
                    permission: "GotoReject|GotoClose|GetPurchaseInvoiceById|UpdateDeductionAndIncreasesOfPurchaseInvoice|UpdateCurrencyInfoCommand|PurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseInvoice/Details/:id",
                  name: $t("PurchaseInvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/PreInvoice/ShowDetails.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), url:'/ProcurementManagement/PreInvoice' },
                      { title: $t("PurchaseInvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseInvoiceDetails"),
                    rule: "editor",
                    permission: "GetPurchaseInvoiceById",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseInvoice/DeductionAndIncrease/:id",
                  name: $t("DeductionAndIncrease"),
                  component: () => import("@/views/ProcurementManagement/DeductionAndIncrease/PurchaseInvoiceDeductionAndIncrease.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), url:'/ProcurementManagement/PreInvoice' },
                      { title: $t("PurchaseInvoiceDetails") },
                      { title: $t("DeductionAndIncrease"), active: true },
                    ],
                    pageTitle: $t("DeductionAndIncrease"),
                    rule: "editor",
                    permission: "UpdateDeductionAndIncreasesOfPurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/PurchaseInvoice/:id",
                  name: $t("PurchaseInvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/PreInvoice/Details.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("PreInvoice"), url:'/ProcurementManagement/PreInvoice' },
                      { title: $t("PurchaseInvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("PurchaseInvoiceDetails"),
                    rule: "editor",
                    permission: "GotoConfirm|GotoReject|GetPurchaseInvoiceById|UpdateItemCodeOfPurchaseOrderItemWithExcel",
                  },
                },
                {
                  path: "/ProcurementManagement/Invoice",
                  name: $t("Invoice"),
                  component: () => import("@/views/ProcurementManagement/Invoice/Invoice.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Invoice"), active: true },
                    ],
                    pageTitle: $t("Invoice"),
                    rule: "editor",
                    permission: "PurchaseInvoice|GetAllPurchaseInvoicesByPaging|UpdateSerialsItemsOfInvoice|UpdateItemCodeOfInvoiceItems|CreateInvoice|UpdateInvoiceItems|UpdateDeductionAndIncreasesOfInvoice|GotoCloseInvoice|GotoConfirmInvoice|GotoRejectInvoice|GotoProcessingInvoice|GotoFinal|GetInvoiceById|GetAllInvoicesByPaging|GetWithRemainingOfCountBy",
                  },
                },
                {
                  path: "/ProcurementManagement/Invoice/:id",
                  name: $t("InvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/Invoice/EditInvoice.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Invoice") , url:'/ProcurementManagement/Invoice' },
                      { title: $t("InvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("InvoiceDetails"),
                    rule: "editor",
                    permission: "GetWithRemainingOfCountBy|GotoCloseInvoice|GotoRejectInvoice|GetInvoiceById|UpdateDeductionAndIncreasesOfInvoice|UpdateSerialsItemsOfInvoice|UpdateItemCodeOfInvoiceItems|PurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/Invoice/ShowDetails/:id",
                  name: $t("InvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/Invoice/ShowDetails.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Invoice")  , url:'/ProcurementManagement/Invoice'},
                      { title: $t("InvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("InvoiceDetails"),
                    rule: "editor",
                    permission: "GetInvoiceById|PurchaseInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/Invoice/DeductionAndIncrease/:id",
                  name: $t("DeductionAndIncrease"),
                  component: () => import("@/views/ProcurementManagement/DeductionAndIncrease/InvoiceDeductionAndIncrease.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Invoice")  , url:'/ProcurementManagement/Invoice'},
                      { title: $t("DeductionAndIncrease"), active: true },
                    ],
                    pageTitle: $t("DeductionAndIncrease"),
                    rule: "editor",
                    permission: "UpdateDeductionAndIncreasesOfInvoice",
                  },
                },
                {
                  path: "/ProcurementManagement/Invoice/Details/:id",
                  name: $t("InvoiceDetails"),
                  component: () => import("@/views/ProcurementManagement/Invoice/Details.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Invoice") },
                      { title: $t("InvoiceDetails"), active: true },
                    ],
                    pageTitle: $t("InvoiceDetails"),
                    rule: "editor",
                    permission: "GotoConfirmInvoice|GotoRejectInvoice|GetInvoiceById",
                  },
                },
                {
                  path: "/ProcurementManagement/DepartmansList",
                  name: $t("Departman"),
                  component: () => import("@/views/ProcurementManagement/Departmans/DepartmansList.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Departman"), active: true },
                    ],
                    pageTitle: $t("Departman"),
                    rule: "editor",
                    permission: "CreateDepartment|ModifyDepartment|GetAllDepartmentsByPaging|Departments",
                  },
                  
                },
                {
                  path: "/ProcurementManagement/CreateDepartman",
                  name: $t("CreateDepartmans"),
                  component: () => import("@/views/ProcurementManagement/Departmans/CreateDepartman.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("CreateDepartmans"), active: true },
                    ],
                    pageTitle: $t("CreateDepartmans"),
                    rule: "editor",
                    permission: "",
                  },
                  
                },
                {
                  path: "/ProcurementManagement/CurrencyExchange",
                  name: $t("CurrencyExchange"),
                  component: () => import("@/views/ProcurementManagement/CurrencyExchange/CurrencyExchangeList.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("CurrencyExchange"), active: true },
                    ],
                    pageTitle: $t("CurrencyExchange"),
                    rule: "editor",
                    permission: "CreateCurrencyExchange|ModifyCurrencyExchange|GetAllCurrencyExchangesByPaging|CurrencyExchange",
                  },
                  
                },
                {
                  path: "/ProcurementManagement/CreateCurrencyExchange",
                  name: $t("CreateCurrencyExchange"),
                  component: () => import("@/views/ProcurementManagement/CurrencyExchange/CreateCurrencyExchange.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("CreateCurrencyExchange"), active: true },
                    ],
                    pageTitle: $t("CreateCurrencyExchange"),
                    rule: "editor",
                    permission: "",
                  },
                  
                },
                {
                  path: "/ProcurementManagement/Units",
                  name: $t("Unit"),
                  component: () => import("@/views/ProcurementManagement/Units/UnitsList.vue"),
                  meta: {
                    breadcrumb: [
                      { title: "Home", url: "/" },
                      { title: $t("ProcurementManagement") },
                      { title: $t("Unit"), active: true },
                    ],
                    pageTitle: $t("Unit"),
                    rule: "editor",
                    permission: "CreateUnitOfMeasure|UnitOfMeasure|GetAllUnitOfMeasuresByPaging|ModifyUnitOfMeasure",
                  },
                  
                },

        ///saleManagement
        {
          path: "/SaleProducts/Index",
          name: $t("Products"),
          component: () => import("@/views/sale-management/Products/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Products"), active: true },
            ],
            pageTitle: $t("Products"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/Vouchers/Index",
          name: $t("Vouchers"),
          component: () => import("@/views/sale-management/Vouchers/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Vouchers"), active: true },
            ],
            pageTitle: $t("Vouchers"),
            rule: "editor",
            permission: "GetAllVoucher|CreateVoucher",
          },
        },
        {
          path: "/Vouchers/Create",
          name: $t("CreateVoucher"),
          component: () =>
            import("@/views/sale-management/Vouchers/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Vouchers"), url: "/Vouchers/Index" },
              { title: $t("CreateVoucher"), active: true },
            ],
            pageTitle: $t("CreateVoucher"),
            rule: "editor",
          },
        },
        {
          path: "/PublicDiscountCodes/Index",
          name: $t("DiscountCodes"),
          component: () =>
            import("@/views/sale-management/PublicDiscountCodes/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("DiscountCodes"), active: true },
            ],
            pageTitle: $t("DiscountCodes"),
            rule: "editor",
            permission: "CreateVoucher",
            // "CreateVoucher|219219|220220"
          },
        },
        {
          path: "/PublicDiscountCodes/Create",
          name: $t("CreateDiscountCode"),
          component: () =>
            import("@/views/sale-management/PublicDiscountCodes/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("DiscountCodes"), url: "/PublicDiscountCodes/Index" },
              { title: $t("CreateDiscountCode"), active: true },
            ],
            pageTitle: $t("CreateDiscountCode"),
            rule: "editor",
          },
        },
        {
          path: "/SaleContracts/Index",
          name: $t("SaleContracts"),
          component: () =>
            import("@/views/sale-management/Contracts/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("SaleContracts"), active: true },
            ],
            pageTitle: $t("SaleContracts"),
            rule: "editor",
            permission:
              "GetAllContract|CreateContract|DeleteContract|GetContractById|ModifyContract",
          },
        },
        {
          path: "/SaleContracts/Management/:id",
          name: $t("SaleContractManagemnet"),
          component: () =>
            import("@/views/sale-management/Contracts/Management/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("SaleContracts"), url: "/SaleContracts/Index" },
              { title: $t("SaleContractManagemnet") },
              { title: "", active: true },
            ],
            pageTitle: $t("SaleContractManagemnet"),
            rule: "editor",
          },
        },
        {
          path: "/Pricing/Index",
          name: $t("Pricing"),
          component: () => import("@/views/sale-management/Pricing/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Pricing"), active: true },
            ],
            pageTitle: $t("Pricing"),
            rule: "editor",
            permission:
              "GetAllPrice|ModifyPricesByExcelFile|GetAllPriceExcelReport|CreatePrice|ModifyPrice",
          },
        },
        // {
        //   path: "/MySaleContracts/Index",
        //   name: $t("MySaleContracts"),
        //   component: () =>
        //     import("@/views/sale-management/MyContracts/Index.vue"),
        //   meta: {
        //     breadcrumb: [
        //       { title: "Home", url: "/" },
        //       { title: $t("SaleManagemnet") },
        //       { title: $t("MySaleContracts"), active: true },
        //     ],
        //     pageTitle: $t("MySaleContracts"),
        //     rule: "editor",
        //     permission: "GetContractsInfoBySaleProductManager",
        //   },
        // },
        {
          path: "/MySaleContracts/Management/:id",
          name: $t("MySaleContractsManagemnet"),
          component: () =>
            import("@/views/sale-management/MyContracts/Management/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("MySaleContracts"), url: "/MySaleContracts/Index" },
              { title: $t("MySaleContractsManagemnet"), active: true },
            ],
            pageTitle: $t("MySaleContractsManagemnet"),
            rule: "editor",
          },
        },
        {
          path: "/DraftOrders/Index",
          name: $t("DraftOrders"),
          component: () =>
            import("@/views/sale-management/DraftOrders/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("DraftOrders"), active: true },
            ],
            pageTitle: $t("DraftOrders"),
            rule: "editor",
            permission:
              "GetAllDraftOrderById|GetClosedBasketById|ChangeDischargePermissionStateToConfirm",
          },
        },
        {
          path: "/DraftOrder/Details/:id",
          name: $t("DraftOrder"),
          component: () =>
            import("@/views/sale-management/DraftOrders/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("DraftOrders"), url: "/DraftOrders/Index" },
              { title: $t("DraftOrder"), active: true },
            ],
            pageTitle: $t("DraftOrder"),
            rule: "editor",
          },
        },
        {
          path: "/DraftOrders/Invoice/:id",
          name: $t("Invoice"),
          component: () =>
            import("@/views/sale-management/DraftOrders/Invoice.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("DraftOrders"), url: "/DraftOrders/Index" },
              { title: $t("Invoice"), active: true },
            ],
            pageTitle: $t("Invoice"),
            rule: "editor",
          },
        },
        {
          path: "/Orders/Index",
          name: $t("Orders"),
          component: () => import("@/views/sale-management/Orders/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Orders"), active: true },
            ],
            pageTitle: $t("Orders"),
            rule: "editor",
            permission: "GetOrderById",
          },
        },
        {
          path: "/Order/Details/:id",
          name: $t("Order"),
          component: () => import("@/views/sale-management/Orders/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Orders"), url: "/Orders/Index" },
              { title: $t("Order"), active: true },
            ],
            pageTitle: $t("Order"),
            rule: "editor",
          },
        },
        {
          path: "/Orders/Invoice/:id",
          name: $t("Invoice"),
          component: () => import("@/views/sale-management/Orders/Invoice.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Orders"), url: "/Orders/Index" },
              { title: $t("Invoice"), active: true },
            ],
            pageTitle: $t("Invoice"),
            rule: "editor",
          },
        },
        {
          path: "/ReturnOrders/Index",
          name: $t("ReturnOrders"),
          component: () =>
            import("@/views/sale-management/ReturnOrders/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("ReturnOrders"), active: true },
            ],
            pageTitle: $t("ReturnOrders"),
            rule: "editor",
            permission: "GetReturnOrderById",
          },
        },
        {
          path: "/ReturnOrders/Details/:id",
          name: $t("ReturnOrdersDetails"),
          component: () =>
            import("@/views/sale-management/ReturnOrders/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("ReturnOrders"), url: "/ReturnOrders/Index" },
              { title: $t("ReturnOrdersDetails"), active: true },
            ],
            pageTitle: $t("ReturnOrdersDetails"),
            rule: "editor",
          },
        },
        {
          path: "/ReturnOrders/Create",
          name: $t("CreateReturnOrders"),
          component: () =>
            import("@/views/sale-management/ReturnOrders/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("ReturnOrders"), url: "/ReturnOrders/Index" },
              { title: $t("CreateReturnOrders"), active: true },
            ],
            pageTitle: $t("CreateReturnOrders"),
            rule: "editor",
          },
        },
        {
          path: "/ReportsOrder/Index",
          name: $t("ReportsOrder"),
          component: () =>
            import("@/views/sale-management/ReportsOrder/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Reports") },
              { title: $t("ReportsOrder"), active: true },
            ],
            pageTitle: $t("ReportsOrder"),
            rule: "editor",
            permission:
              "GetAllSaleAgentQuotaByPageInfo|GetSaleReportChartData|GetSaleReportChartHeaderData",
            // "216216|218218"
          },
        },
        {
          path: "/ReportsDraftOrder/Index",
          name: $t("ReportsDraftOrder"),
          component: () =>
            import("@/views/sale-management/ReportsDraftOrder/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Reports") },
              { title: $t("ReportsDraftOrder"), active: true },
            ],
            pageTitle: $t("ReportsDraftOrder"),
            rule: "editor",
            permission:
              "GetAllSaleAgentQuotaByPageInfo|GetSaleReportChartData|GetSaleReportChartHeaderData",
            // "216216|218218"
          },
        },
        {
          path: "/SalesPersons/Index",
          name: $t("SalesPersons"),
          component: () =>
            import("@/views/sale-management/SalesPersons/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SalesPersons") },
              { title: $t("SalesPersons"), active: true },
            ],
            pageTitle: $t("SalesPersons"),
            rule: "editor",
            permission:
              "GetAllAgentSegmentInfos|GetAgentInfosOfCurrentUser|CreateSalePerson|DeleteSalePerson|GetAgentsOfSalePerson|GetAllAgentSegmentInfos|RemoveAgentInfoFromSalePerson|AddAgentInfoToSalePerson|AddSaleProductManagerSegmentInfo|RemoveSaleProductManagerSegmentInfo",
          },
        },
        {
          path: "/BasicDefinitions/AssignProductToSegment/Index",
          name: $t("AssignProductToSegment"),
          component: () =>
            import(
              "@/views/sale-management/BasicDefinitions/AssignProductToSegment/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("AssignProductToSegment") },
              { title: $t("AssignProductToSegment"), active: true },
            ],
            pageTitle: $t("AssignProductToSegment"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/Quotas/Index",
          name: $t("Quotas"),
          component: () => import("@/views/sale-management/Quotas/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Quotas"), active: true },
            ],
            pageTitle: $t("Quotas"),
            rule: "editor",
            permission:
              "ChargeByExcelFile|GetAllQuotaTransactionItem|DischargeByExcelFile|GetAllSaleAgentQuotaWithKeyword",
          },
        },
        {
          path: "/Quotas/Charge",
          name: $t("ChargeQuota"),
          component: () => import("@/views/sale-management/Quotas/AddChargePage.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Quotas"), url: "/Quotas/Index" },
              { title: $t("ChargeQuota"), active: true },
            ],
            pageTitle: $t("ChargeQuota"),
            rule: "editor",
          },
        },
        {
          path: "/Quotas/Discharge",
          name: $t("DischargeQuota"),
          component: () => import("@/views/sale-management/Quotas/AddDischargePage.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Quotas"), url: "/Quotas/Index" },
              { title: $t("DischargeQuota"), active: true },
            ],
            pageTitle: $t("DischargeQuota"),
            rule: "editor",
          },
        },
        {
          path: "/Quotas/Details/:id",
          name: $t("AgentQuotas"),
          component: () => import("@/views/sale-management/Quotas/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("Quotas"), url: "/Quotas/Index" },
              { title: $t("QuotaDetails"), active: true },
            ],
            pageTitle: $t("QuotaDetails"),
            rule: "editor",
          },
        },
        {
          path: "/AgentQuotas/:id",
          name: $t("AgentQuotas"),
          component: () =>
            import("@/views/sale-management/Quotas/AgentQuotas.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("AgentQuotas"), active: true },
            ],
            pageTitle: $t("AgentQuotas"),
            rule: "editor",
          },
        },
        {
          path: "/SaleProductManagers/Index",
          name: $t("SaleProductManagers"),
          component: () =>
            import("@/views/sale-management/SaleProductManagers/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleProductManagers") },
              { title: $t("SaleProductManagers"), active: true },
            ],
            pageTitle: $t("SaleProductManagers"),
            rule: "editor",
            permission:
              "DeleteSaleProductManager|CreateSaleProductManager|GetSegmentsInfoBySaleProductManager|AddSaleProductManagerSegmentInfo|RemoveSaleProductManagerSegmentInfo|AddSaleProductManagerContractInfo|RemoveSaleProductManagerContractInfo|GetAllSaleProductManager|GetContractsInfoBySaleProductManager",
          },
        },
        {
          path: "/BasicDefinitions/Index",
          name: $t("BasicDefinitions"),
          component: () =>
            import("@/views/sale-management/BasicDefinitions/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("BasicDefinitions"), active: true },
            ],
            pageTitle: $t("BasicDefinitions"),
            rule: "editor",
            permission:
              "GetAllBeneficiaryRuleWithPaging|CreatePromoterPlan|ModifyName|RemovePromoterPlan|CreateSegment|ModifySegment|RemoveSegment|GetAllProductInfos|RemoveProductInfo|AddProductInfo|UnAssignBeneficiaryRule|AssignBeneficiaryRule",
          },
        },
        {
          path: "/SaleTargets/Index",
          name: $t("SaleTargets"),
          component: () =>
            import("@/views/sale-management/SaleTargets/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("SaleTargets"), active: true },
            ],
            pageTitle: $t("SaleTargets"),
            rule: "editor",
          },
        },
        {
          path: "/SaleTargets/:saleTargetId/Segments",
          name: $t("SaleTargetsSegments"),
          component: () =>
            import("@/views/sale-management/SaleTargets/Segments.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("SaleTargets"), url: "/SaleTargets/Index" },
              { title: $t("ProductSegments"), active: true },
            ],
            pageTitle: $t("SaleTargetsSegments"),
            rule: "editor",
          },
        },
        {
          path: "/SaleTargets/:saleTargetId/Segments/:segmentTargetId/SaleAgents",
          name: "SaleTargetSegmentSaleAgents",
          component: () =>
            import("@/views/sale-management/SaleTargets/SaleAgents.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("SaleManagemnet") },
              { title: $t("SaleTargets"), url: "/SaleTargets/Index" },
              {
                title: $t("ProductSegments"),
                url: (route) => {
                  return `/SaleTargets/${route.params.saleTargetId}/Segments`;
                },
              },
              { title: $t("SaleAgents"), active: true },
            ],
            pageTitle: $t("SaleAgents"),
            rule: "editor",
          },
        },
        ///OrderManagement
        {
          path: "/WebSiteBaskets/Index",
          name: $t("WebSiteBaskets"),
          component: () =>
            import("@/views/basket-management/WebSiteBaskets/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("WebSiteBaskets"), active: true },
            ],
            pageTitle: $t("WebSiteBaskets"),
            rule: "editor",
            permission:
              "GetClosedBasketById|ChangeStateToDeliver|ChangeStateToRejectForBusinessToCustomerBasket|CreateCashBankPayment|",
          },
        },
        {
          path: "/WebSiteBaskets/BasketDetails/:tabIndex/:id",
          name: $t("BasketDetails"),
          component: () =>
            import("@/views/basket-management/WebSiteBaskets/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("WebSiteBaskets"), url: "/WebSiteBaskets/Index" },
              { title: $t("BasketDetails"), active: true },
            ],
            pageTitle: $t("BasketDetails"),
            rule: "editor",
          },
        },
        {
          path: "/ChannelBaskets/Index",
          name: $t("ChannelOrders"),
          component: () =>
            import("@/views/basket-management/ChannelBaskets/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("ChannelBaskets"), active: true },
            ],
            pageTitle: $t("ChannelBaskets"),
            rule: "editor",
            permission:
              "ApplyImmediateDiscountToBasket|GetSaleAgentBasketById|ChangeStateToDeliver",
          },
        },
        {
          path: "/ChannelBaskets/BasketDetails/:tabIndex/:id",
          name: $t("BasketDetails"),
          component: () =>
            import("@/views/basket-management/ChannelBaskets/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("ChannelBaskets"), url: "/ChannelBaskets/Index" },
              { title: $t("BasketDetails"), active: true },
            ],
            pageTitle: $t("BasketDetails"),
            rule: "editor",
          },
        },
        {
          path: "/ChannelBaskets/AddBasket",
          name: $t("AddBasket"),
          component: () =>
            import("@/views/basket-management/ChannelBaskets/AddBasket.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("ChannelBaskets"), url: "/ChannelBaskets/Index" },
              { title: $t("AddBasket"), active: true },
            ],
            pageTitle: $t("AddBasket"),
            rule: "editor",
          },
        },
        {
          path: "/ChannelBaskets/EditBasket/:id",
          name: $t("EditBasket"),
          component: () =>
            import("@/views/basket-management/ChannelBaskets/EditBasket.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("BasketManagement") },
              { title: $t("ChannelBaskets"), url: "/ChannelBaskets/Index" },
              { title: $t("EditBasket"), active: true },
            ],
            pageTitle: $t("EditBasket"),
            rule: "editor",
          },
        },
        
        ////FinancialManagement
        {
          path: "/BankPortalPayments/Index",
          name: $t("BankPortalPayments"),
          component: () =>
            import("@/views/financial-management/BankPortalPayments/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("BankPortalPayments"), active: true },
            ],
            pageTitle: $t("BankPortalPayments"),
            rule: "editor",
            permission: "GetALLBankPayment",
          },
        },

        {
          path: "/Receipts/Index",
          name: $t("Receipts"),
          component: () =>
            import("@/views/financial-management/ReceiptsAndPayments/Receipts/Receipts.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("Receipts"), active: true },
            ],
            pageTitle: $t("Receipts"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/Payments/Index",
          name: $t("Payments"),
          component: () =>
            import("@/views/financial-management/ReceiptsAndPayments/Payments/Payments.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("Payments"), active: true },
            ],
            pageTitle: $t("Payments"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/Party/Index",
          name: $t("Party"),
          component: () =>
            import("@/views/financial-management/Party/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("Party"), active: true },
            ],
            pageTitle: $t("Party"),
            rule: "editor",
            permission: "CreateCostCenter|AssignAccountReference|DeleteCostCenter|GetAllByPaging|GetAllAccountReferenceInfos|GetAllCostCentersByParentAccountId|GetPartyAccount|GetAllSubsidiaryAndDetailAccounts|GetAllAccountsOfPartiesInCreditManagement|GetAllPartiesByNameInCreditManagement|GetAllAccountsOfPartiesByNameInCreditManagement|GetPartyAccountsByOwner",
          },
        },
        {
          path: "/Party/addCostTitle/:id",
          name: $t("Party"),
          component: () =>
            import("@/views/financial-management/Party/AddCostTitleToPartyAccount.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("Party")  , url:"/Party/Index"},
              { title: $t("Connections"), active: true },
            ],
            pageTitle: $t("Party"),
            rule: "editor",
            permission: "CreateCostCenter|AssignAccountReference|DeleteCostCenter|GetAllByPaging|GetAllAccountReferenceInfos|GetAllCostCentersByParentAccountId|GetPartyAccount|GetAllSubsidiaryAndDetailAccounts|GetAllAccountsOfPartiesInCreditManagement|GetAllPartiesByNameInCreditManagement|GetAllAccountsOfPartiesByNameInCreditManagement|GetPartyAccountsByOwner",
          },
        },
        {
          path: "/PettyCash/Reports",
          name: $t("Reports"),
          component: () =>
            import("@/views/financial-management/PettyCash/PettyCashReports.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialManagement") },
              { title: $t("Reports"), active: true },
            ],
            pageTitle: $t("Reports"),
            rule: "editor",
            permission: "Report",
          },
        },
        ////ShippingManagement
        {
          path: "/Operators/Index",
          name: $t("Operators"),
          component: () =>
            import("@/views/shipping-management/Operators/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Operators"), active: true },
            ],
            pageTitle: $t("Operators"),
            rule: "editor",
            permission: "GetAllOperator|CreateOperator",
          },
        },
        {
          path: "/TransportTools/Index",
          name: $t("TransportTools"),
          component: () =>
            import("@/views/shipping-management/TransportTools/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("TransportTools"), active: true },
            ],
            pageTitle: $t("TransportTools"),
            rule: "editor",
            permission: "CreateBike|CreateTruck|GetAllTransport",
          },
        },
        {
          path: "/TransportFleets/Index",
          name: $t("TransportFleet"),
          component: () =>
            import("@/views/shipping-management/TransportFleets/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("TransportFleets"), active: true },
            ],
            pageTitle: $t("TransportFleets"),
            rule: "editor",
            permission: "CreateTransportFleet|GetAllTransportsFleet",
          },
        },
        {
          path: "/Package/Index",
          name: $t("Packages"),
          component: () =>
            import("@/views/shipping-management/Package/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Packages"), active: true },
            ],
            pageTitle: $t("Packages"),
            rule: "editor",
            permission:
              "GetAllPackage|AssignOperatorToPackage|ChangePackageStateToFinished|CreatePackage|ChangePackageStateToOperatorAssigne|ChangePackageStateToOperatorApproved|ChangePackageStateToDelivered|GetPackageById|GetAllByOperator|GetAllByOwner|SetPackageReceiptImage|AssignTransportFleetToPackage|GetTinyPackageInfoById|GetAllTinyInfoByOperator|GetPackageReceiptImageByCheckoutId|GetExcelOfPackageItems|GetSerialsOfPackageItemExcel|UpdatePackageCartonsCount|ChangePackageStateToReject|GetPackagesWithExcel|GetPackagesStateInfoForDashboardFromFirstOfMonth",
          },
        },
        {
          path: "/shipping-management/RecievePackage/Index",
          name: $t("Packages"),
          component: () =>
            import("@/views/shipping-management/RecievePackage/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Packages"), active: true },
            ],
            pageTitle: $t("Packages"),
            rule: "editor",
            permission:
              "GetAllPackage|AssignOperatorToPackage|ChangePackageStateToFinished",
          },
        },
        {
          path: "/Package/Details/:tabIndex/:id",
          name: $t("Details"),
          component: () =>
            import("@/views/shipping-management/Details_Package1/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: "", url: "" },
              { title: "", active: true },
            ],
            pageTitle: $t("Details"),
            rule: "editor",
            permission: "",
          },
        },
        {
          path: "/OperationOperators/Index",
          name: $t("OperationOperators"),
          component: () =>
            import("@/views/shipping-management/OperationOperators/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("OperationOperators"), active: true },
            ],
            pageTitle: $t("OperationOperators"),
            rule: "editor",
            permission:
              "GetAllByOperator|ChangePackageStateToOperatorApproved|ChangePackageStateToDelivered",
          },
        },
        {
          path: "/shipping-management/PurchaseInvoice/Index",
          name: $t("PurchaseInvoice"),
          component: () =>
            import("@/views/shipping-management/Procurement/PurchaseInvoice/PurchaseInvoice.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("PurchaseInvoice"), active: true },
            ],
            pageTitle: $t("PurchaseInvoice"),
            rule: "editor",
            permission:
              "CreateReceiveShipment",
          },
        },
        {
          path: "/shipping-management/Recieve/Index",
          name: $t("Recieve"),
          component: () =>
            import("@/views/shipping-management/Procurement/Cargo/Recieve/Recieve.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Cargo") },
              { title: $t("Recieve"), active: true },
            ],
            pageTitle: $t("Recieve"),
            rule: "editor",
            permission:
              "GetAllReceiveShipmentsByPaging|CreateReceiveShipment|GetReceiveShipmentById|GetAllReceiveShipmentsByPaging|GetWithRemainingCountOfReceiveShipmentBy|UpdateSerialsItemsOfShipment|UpdateDeductionAndIncreasesOfShipment|UpdateShipmentCurrencyInfo|GotoConfirmShipment|GotoDeliverShipment|UpdatePassagePermit|GotoCloseShipment",
          },
        },
        {
          path: "/shipping-management/Recieve/PackingList/:id",
          name: $t("PackingList"),
          component: () =>
            import("@/views/shipping-management/Procurement/Cargo/Recieve/PackingList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Cargo") },
              { title: $t("Recieve"), url:'/shipping-management/Recieve/Index' },
              { title: $t("PackingList"), active: true },
            ],
            pageTitle: $t("PackingList"),
            rule: "editor",
            permission:
              "GetAllReceiveShipmentsByPaging",
          },
        },
        {
          path: "/shipping-management/Send/Index",
          name: $t("Send"),
          component: () =>
            import("@/views/shipping-management/Procurement/Cargo/Send/Send.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Cargo") },
              { title: $t("Send"), active: true },
            ],
            pageTitle: $t("Send"),
            rule: "editor",
            permission:
              "GetAllSendShipmentsByPaging|GetSendShipmentById|UpdateSerialsItemsOfShipment|UpdateDeductionAndIncreasesOfShipment|UpdateShipmentCurrencyInfo|GotoConfirmShipment|GotoDeliverShipment|UpdatePassagePermit|GotoCloseShipment",
          },
        },
        {
          path: "/shipping-management/Send/:id",
          name: $t("Send"),
          component: () =>
            import("@/views/shipping-management/Procurement/Cargo/Send/ShowDetails.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Cargo") },
              { title: $t("Send"), active: true },
            ],
            pageTitle: $t("Send"),
            rule: "editor",
            permission:
              "GetAllSendShipmentsByPaging|GetSendShipmentById|UpdateSerialsItemsOfShipment|UpdateDeductionAndIncreasesOfShipment|UpdateShipmentCurrencyInfo|GotoConfirmShipment|GotoDeliverShipment|UpdatePassagePermit|GotoCloseShipment",
          },
        },
        {
          path: "/shipping-management/RemainingPurchaseInvoice/:id",
          name: $t("PurchaseInvoiceDetails"),
          component: () => import("@/views/shipping-management/Procurement/PurchaseInvoice/EditPurchaseInvoice.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("PreInvoice"), url:'/shipping-management/PurchaseInvoice/Index' },
              { title: $t("PurchaseInvoiceDetails"), active: true },
            ],
            pageTitle: $t("PurchaseInvoiceDetails"),
            rule: "editor",
            permission: "CreateReceiveShipment",
          },
        },
        {
          path: "/shipping-management/RemainingShipment/:id",
          name: $t("RecieveDetails"),
          component: () => import("@/views/shipping-management/Procurement/Cargo/Recieve/EditRecieve.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Recieve"), url:'/shipping-management/Recieve/Index' },
              { title: $t("RecieveDetails"), active: true },
            ],
            pageTitle: $t("RecieveDetails"),
            rule: "editor",
            permission: "GetReceiveShipmentById|UpdateSerialsItemsOfShipment|UpdateDeductionAndIncreasesOfShipment|UpdateShipmentCurrencyInfo|GotoConfirmShipment|CreatePackage",
          },
        },
        {
          path: "/shipping-management/Details/:id",
          name: $t("RecieveDetails"),
          component: () => import("@/views/shipping-management/Procurement/Cargo/Recieve/ConfirmDetails.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Recieve"), url:'/shipping-management/Recieve/Index' },
              { title: $t("RecieveDetails"), active: true },
            ],
            pageTitle: $t("RecieveDetails"),
            rule: "editor",
            permission: "GetReceiveShipmentById|UpdateSerialsItemsOfShipment|UpdateDeductionAndIncreasesOfShipment|UpdateShipmentCurrencyInfo|GotoConfirmShipment|CreatePackage",
          },
        },
        {
          path: "/shipping-management/Recieve/DeductionAndIncrease/:id",
          name: $t("DeductionAndIncrease"),
          component: () => import("@/views/ProcurementManagement/DeductionAndIncrease/ShipmentDeductionAndIncrease.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ShippingManagement") },
              { title: $t("Recieve"), url:'/shipping-management/Recieve/Index' },
              { title: $t("RecieveDetails") },
              { title: $t("DeductionAndIncrease"), active: true },
            ],
            pageTitle: $t("DeductionAndIncrease"),
            rule: "editor",
            permission: "UpdateDeductionAndIncreasesOfShipment",
          },
        },
        ////customerManagement
        {
          path: "/Customers/Index",
          name: $t("Customers"),
          component: () =>
            import("@/views/businessParties-management/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Customers"), active: true },
            ],
            pageTitle: $t("Customers"),
            rule: "editor",
            permission: "GetAllCustomersByType",
            // permission: "CreateIndividualCustomer|CreateCustumerLegal"
          },
        },
        {
          path: "/Customers/CreateIndividual",
          name: $t("CreateIndividualCustomer"),
          component: () =>
            import("@/views/businessParties-management/Individual/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Customers"), url: "/Customers/Index" },
              { title: $t("CreateIndividualCustomer"), active: true },
            ],
            pageTitle: $t("CreateIndividualCustomer"),
            rule: "editor",
          },
        },
        {
          path: "/Customers/CreateLegal",
          name: $t("CreateLegalCustomer"),
          component: () =>
            import("@/views/businessParties-management/Legal/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Customers"), url: "/Customers/Index" },
              { title: $t("CreateLegalCustomer"), active: true },
            ],
            pageTitle: $t("CreateLegalCustomer"),
            rule: "editor",
          },
        },
        {
          path: "/Customers/EditLegal/:id",
          name: $t("EditCustomer"),
          component: () =>
            import("@/views/businessParties-management/Legal/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Customers"), url: "/Customers/Index" },
              { title: $t("EditCustomer"), active: true },
            ],
            pageTitle: $t("EditCustomer"),
            rule: "editor",
          },
        },
        {
          path: "/Customers/EditIndividual/:id",
          name: $t("EditCustomer"),
          component: () =>
            import("@/views/businessParties-management/Individual/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Customers"), url: "/Customers/Index" },
              { title: $t("EditCustomer"), active: true },
            ],
            pageTitle: $t("EditCustomer"),
            rule: "editor",
          },
        },
        {
          path: "/CustomersSetting/Index",
          name: $t("Setting"),
          component: () =>
            import("@/views/businessParties-management/Setting/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Setting"), active: true },
            ],
            pageTitle: $t("Setting"),
            rule: "editor",
            permission: "GetAllDocumentSetting",
          },
        },
        {
          path: "/Agents/Index",
          name: $t("Agents"),
          component: () =>
            import("@/views/businessParties-management/Agents/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), active: true },
            ],
            pageTitle: $t("Agents"),
            rule: "editor",
            permission:
              "GetAllByAgentInfo||CreateAgentLegal|CreateAgentIndividual|GetAllSaleAgentQuota|GetAgentRecipientsByOwnerIdInShipping|GetAgentById",
          },
        },

        {
          path: "/Agents/CreateBranch/:id",
          name: $t("CreateBranchAgent"),
          component: () =>
            import(
              "@/views/businessParties-management/Agents/CreateBranch.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("Branch"), active: true },
            ],
            pageTitle: $t("Branch"),
            rule: "editor",
            permission: "CreateAgentLegal",
          },
        },
        {
          path: "/Agents/CreateIndividual",
          name: $t("CreateIndividualAgent"),
          component: () =>
            import(
              "@/views/businessParties-management/Agents/Individual/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("CreateIndividualAgent"), active: true },
            ],
            pageTitle: $t("CreateIndividualAgent"),
            rule: "editor",
          },
        },
        {
          path: "/Agents/ConfirmIndividual/:id",
          name: $t("EditAgent"),
          component: () =>
            import(
              "@/views/businessParties-management/Agents/Individual/Confirm.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("ConfirmIndividualAgent"), active: true },
            ],
            pageTitle: $t("ConfirmIndividualAgent"),
            rule: "editor",
          },
        },
        {
          path: "/Agents/EditIndividual/:id",
          name: $t("EditAgent"),
          component: () =>
            import(
              "@/views/businessParties-management/Agents/Individual/Edit.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("EditIndividualAgent"), active: true },
            ],
            pageTitle: $t("EditIndividualAgent"),
            rule: "editor",
          },
        },
        {
          path: "/Agents/CreateLegal",
          name: $t("CreateLegalAgent"),
          component: () =>
            import(
              "@/views/businessParties-management/Agents/Legal/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("CreateLegalAgent"), active: true },
            ],
            pageTitle: $t("CreateLegalAgent"),
            rule: "editor",
          },
        },
        {
          path: "/Agents/EditLegal/:id",
          name: $t("EditAgent"),
          component: () =>
            import("@/views/businessParties-management/Agents/Legal/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("EditLegalAgent"), active: true },
            ],
            pageTitle: $t("EditLegalAgent"),
            rule: "editor",
          },
        },
        {
          path: "/Agents/ConfirmLegal/:id",
          name: $t("EditAgent"),
          component: () =>
            import("@/views/businessParties-management/Agents/Legal/Confirm.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Agents"), url: "/Agents/Index" },
              { title: $t("ConfirmLegalAgent"), active: true },
            ],
            pageTitle: $t("ConfirmLegalAgent"),
            rule: "editor",
          },
        },

        {
          path: "/SalesPartners/Index",
          name: $t("SalesPartners"),
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), active: true },
            ],
            pageTitle: $t("SalesPartners"),
            rule: "editor",
            permission:
              "CreatePromoterIndividual|CreatePromoterLegal|AssignPromoterPlan|UnAssignPromoterPlan|GetPartyAccountsByOwner|GetAllSoldItemsByOwner",
            // "CreatePromoterIndividual|CreatePromoterLegal|AssignPromoterPlan|UnAssignPromoterPlan|GetPartyAccountsByOwner|GetAllFinancialTransactions|GetAllSoldItemsByOwner"
          },
        },
        {
          path: "/SalesPartners/ShowDetailsSalePartner/:id",
          name: "پروفایل همیار فروش",
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/ShowDetailsSalePartner.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), url: "/SalesPartners/Index" },
              { title: "پروفایل همیار فروش", active: true },
            ],
            pageTitle: "پروفایل همیار فروش",
            rule: "editor",
          },
        },
        {
          path: "/SalesPartners/CreateIndividual",
          name: "درج همیار فروش حقیقی",
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/Individual/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), url: "/SalesPartners/Index" },
              { title: "درج همیار فروش حقیقی", active: true },
            ],
            pageTitle: "درج همیار فروش حقیقی",
            rule: "editor",
          },
        },
        {
          path: "/SalesPartners/EditIndividual/:id",
          name: $t("EditSalesPartners"),
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/Individual/Edit.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), url: "/SalesPartners/Index" },
              { title: $t("EditIndividualSalesPartners"), active: true },
            ],
            pageTitle: $t("EditIndividualSalesPartners"),
            rule: "editor",
          },
        },
        {
          path: "/SalesPartners/CreateLegal",
          name: "درج همیار فروش حقوقی",
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/Legal/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), url: "/SalesPartners/Index" },
              { title: "درج همیار فروش حقوقی", active: true },
            ],
            pageTitle: "درج همیار فروش حقوقی",
            rule: "editor",
          },
        },
        {
          path: "/SalesPartners/EditLegal/:id",
          name: $t("EditSalesPartners"),
          component: () =>
            import(
              "@/views/businessParties-management/SalesPartners/Legal/Edit.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("SalesPartners"), url: "/SalesPartners/Index" },
              { title: $t("EditLegalSalesPartners"), active: true },
            ],
            pageTitle: $t("EditLegalSalesPartners"),
            rule: "editor",
          },
        },

        {
          path: "/Parties/Index",
          name: $t("Parties"),
          component: () =>
            import("@/views/businessParties-management/Parties/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Parties"), active: true },
            ],
            pageTitle: $t("Parties"),
            rule: "editor",
            permission: "GetALLParty",
          },
        },
        /////ُSuppliers
        {
          path: "/Suppliers/Index",
          name: $t("Suppliers"),
          component: () =>
            import("@/views/businessParties-management/Suppliers/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Suppliers"), active: true },
            ],
            pageTitle: $t("Suppliers"),
            rule: "editor",
            permission:
              "GetAllByAgentInfo||CreateAgentLegal|CreateAgentIndividual|GetAllSaleAgentQuota|GetAgentRecipientsByOwnerIdInShipping|GetAgentById",
          },
        },
        {
          path: "/Suppliers/CreateIndividual",
          name: $t("CreateIndividualSupplier"),
          component: () =>
            import(
              "@/views/businessParties-management/Suppliers/Individual/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Suppliers"), url: "/Suppliers/Index" },
              { title: $t("CreateIndividualSupplier"), active: true },
            ],
            pageTitle: $t("CreateIndividualSupplier"),
            rule: "editor",
          },
        },
        {
          path: "/Suppliers/CreateLegal",
          name: $t("CreateLegalSupplier"),
          component: () =>
            import(
              "@/views/businessParties-management/Suppliers/Legal/Create.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Suppliers"), url: "/Suppliers/Index" },
              { title: $t("CreateLegalSupplier"), active: true },
            ],
            pageTitle: $t("CreateLegalSupplier"),
            rule: "editor",
          },
        },
        {
          path: "/Suppliers/EditLegal/:id",
          name: $t("EditSupplier"),
          component: () =>
            import("@/views/businessParties-management/Suppliers/Legal/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Suppliers"), url: "/Suppliers/Index" },
              { title: $t("EditSupplier"), active: true },
            ],
            pageTitle: $t("EditSupplier"),
            rule: "editor",
          },
        },
        {
          path: "/Suppliers/EditIndividual/:id",
          name: $t("EditSupplier"),
          component: () =>
            import("@/views/businessParties-management/Suppliers/Individual/Edit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت طرفهای تجاری" },
              { title: $t("Suppliers"), url: "/Suppliers/Index" },
              { title: $t("EditSupplier"), active: true },
            ],
            pageTitle: $t("EditSupplier"),
            rule: "editor",
          },
        },
        ///CreditManagement
        {
          path: "/CreditManagement/Transactions/Index",
          name: "تراکنش‌ها",
          component: () =>
            import("@/views/Credit-management/Transactions/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "تراکنش‌ها", active: true },
            ],
            pageTitle: "تراکنش‌ها",
            rule: "editor",
            permission: "GetAllFinancialTransactions",
          },
        },
        {
          path: "/CreditManagement/CreditInquiry/Index",
          name: "مانده اعتبارات",
          component: () =>
            import("@/views/Credit-management/CreditInquiry/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "مانده اعتبارات", active: true },
            ],
            pageTitle: "مانده اعتبارات",
            rule: "editor",
            permission: "GetAllAccountsOfPartiesByNameInCreditManagement",
          },
        },
        {
          path: "/CreditManagement/AccountGroup/Index",
          name: "گروه حساب ها",
          component: () =>
            import("@/views/Credit-management/AccountGroup/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "گروه حساب ها", active: true },
            ],
            pageTitle: "گروه حساب ها",
            rule: "editor",
            permission: "CreateGroupAccount|GetAllGroupsAccounts",
          },
        },
        {
          path: "/CreditManagement/GeneralAccount/Index",
          name: "حساب کل",
          component: () =>
            import("@/views/Credit-management/GeneralAccount/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب کل", active: true },
            ],
            pageTitle: "حساب کل",
            rule: "editor",
            permission: "CreateGeneralAccount|GetAllGeneralsAccounts",
          },
        },
        {
          path: "/CreditManagement/SubsidiaryAccount/Index",
          name: "حساب معین",
          component: () =>
            import("@/views/Credit-management/SubsidiaryAccount/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب معین", active: true },
            ],
            pageTitle: "حساب معین",
            rule: "editor",
            permission: "CreateSubsidiaryAccount|GetAllSubsidiaryAccounts",
          },
        },
        {
          path: "/CreditManagement/DetailsAccount/Index",
          name: "حساب تفضیلی",
          component: () =>
            import("@/views/Credit-management/DetailsAccount/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب تفضیلی", active: true },
            ],
            pageTitle: "حساب تفضیلی",
            rule: "editor",
            permission: "CreateDetailAccount|GetAllDetailsAccounts",
          },
        },
        {
          path: "/CreditManagement/FloatingAccount/Index",
          name: "حساب شناوری",
          component: () =>
            import("@/views/Credit-management/FloatingAccount/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب شناوری", active: true },
            ],
            pageTitle: "حساب شناوری",
            rule: "editor",
            permission: "GetAllSubsidiaryAndDetailAccounts|CreateFloatingAccount|AssignConnectedAccounts|GetAllFirstLevelFloatingAccounts|GetAllChildAccounts|GetAllAccountsByParentAccountId|GetAllLastLevelOfFloatingAccounts",
          },
        },
        {
          path: "/CreditManagement/FloatingAccount/Subcategory/:id",
          name: "حساب شناوری",
          component: () =>
            import("@/views/Credit-management/FloatingAccount/FloatingAccountChilds.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب شناوری" , url:"/CreditManagement/FloatingAccount/Index"},
              { title: "زیرمجموعه حساب شناوری", active: true },
            ],
            pageTitle: "زیرمجموعه حساب شناوری",
            rule: "editor",
            permission: "GetAllSubsidiaryAndDetailAccounts|AssignConnectedAccounts|GetAllChildAccounts|GetAllAccountsByParentAccountId|GetAllLastLevelOfFloatingAccounts",
          },
        },
        {
          path: "/CreditManagement/FloatingAccount/Subcategory/Connections/:id",
          name: "ارتباطات",
          component: () =>
            import("@/views/Credit-management/FloatingAccount/Connections.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "حساب ها" },
              { title: "حساب شناوری" , url:"/CreditManagement/FloatingAccount/Index"},
              { title: "زیرمجموعه حساب شناوری" },
              { title: "ارتباطات", active: true },
            ],
            pageTitle: "ارتباطات",
            rule: "editor",
            permission: "GetAllSubsidiaryAndDetailAccounts|AssignConnectedAccounts|GetAllFirstLevelFloatingAccounts|GetAllChildAccounts|GetAllAccountsByParentAccountId|GetAllLastLevelOfFloatingAccounts",
          },
        },
        {
          path: "/CreditManagement/CostCenter/Index",
          name: " مراکز هزینه",
          component: () =>
            import("@/views/Credit-management/CostCenter/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "مراکز هزینه", active: true },
            ],
            pageTitle: " مراکز هزینه",
            rule: "editor",
            permission: "GetAllPettyCashes|AssignAccounts|GetAllAccounts|GetAllSubsidiaryAndDetailAccounts|CreateCostCenter|AssignAccountReference|DeleteCostCenter|GetAllByPaging|GetAllAccountReferenceInfos|GetAllCostCentersByParentAccountId",
          },
        },
        {
          path: "/CreditManagement/CostCenter/Connections/:id",
          name: "ارتباطات",
          component: () =>
            import("@/views/Credit-management/CostCenter/Connections.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "مراکز هزینه" ,url:"/CreditManagement/CostCenter/Index"},
              { title: "ارتباطات", active: true },
            ],
            pageTitle: "ارتباطات",
            rule: "editor",
            permission: "GetAllPettyCashes|AssignAccounts|GetAllAccounts|GetAllSubsidiaryAndDetailAccounts|AssignAccountReference|GetAllByPaging|GetAllAccountReferenceInfos|GetAllCostCentersByParentAccountId",
          },
        },
        {
          path: "/CreditManagement/CostTitle/Index",
          name: "CostTitle",
          component: () =>
            import("@/views/Credit-management/CostTitle/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "عنوان هزینه", active: true },
            ],
            pageTitle: "عنوان هزینه",
            rule: "editor",
            permission: "GetAllAccountsOfPartiesByNameInCreditManagement",
          },
        },
        {
          path: "/CreditManagement/CostTitle/Connections/:id",
          name: "ارتباطات",
          component: () =>
            import("@/views/Credit-management/CostTitle/Connections.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "CostTitle" },
              { title: "ارتباطات", active: true },
            ],
            pageTitle: "ارتباطات",
            rule: "editor",
            permission: "GetAllAccountsOfPartiesByNameInCreditManagement",
          },
        },
        {
          path: "/CreditManagement/Voucher/Index",
          name: "اسناد مالی",
          component: () =>
            import("@/views/financial-management/Vouchers/Voucher.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "اسناد مالی", active: true },
            ],
            pageTitle: "اسناد مالی",
            rule: "editor",
            permission: "Vouchers|GetAllVouchers|GetAllArticles",
          },
        },
        {
          path: "/CreditManagement/Voucher/Article/:id",
          name: "آرتیکل ها",
          component: () =>
            import("@/views/financial-management/Vouchers/Article.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "مدیریت اعتبارات" },
              { title: "آرتیکل ها", active: true },
            ],
            pageTitle: "آرتیکل ها",
            rule: "editor",
            permission: "GetAllArticles",
          },
        },
        {
          path: "/PettyCash/Index",
          name: $t("PettyCash"),
          component: () =>
            import("@/views/financial-management/PettyCash/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("PettyCash"), active: true },
            ],
            pageTitle: $t("PettyCash"),
            rule: "editor",
            permission: "GetAllSubsidiaryAndDetailAccounts|GetAllPettyCashes|AssignAccounts|GetAllAccounts",
          },
        },

        {
          path: "/PettyCash/Payment/:id",
          name: $t("PettycashPayment"),
          component: () =>
            import("@/views/financial-management/PettyCash/PettyCashPayment.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount") , url:"/PettyCash/ChargePettycashsAccount"},
              { title: $t("PettycashPayment"), active: true },
            ],
            pageTitle: $t("PettycashPayment"),
            rule: "editor",
            permission: "CreateExpense|GetAllSubsidiaryAndDetailAccounts|GetAllExpense|GetAllExpenseByFundReplenishmentId|GetAllFiles|AddFileAttachmentCommand",
          },
        },
        {
          path: "/PettyCash/PaymentAttachment/:id",
          name: $t("PettycashPayment"),
          component: () =>
            import("@/views/financial-management/PettyCash/PaymentAttachment.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount") },
              { title: $t("PettycashPayment") },
              { title: $t("Attachment"), active: true },
            ],
            pageTitle: $t("PettycashPayment"),
            rule: "editor",
            permission: "GetAllFiles|AddFileAttachmentCommand",
          },
        },
        {
          path: "/PettyCash/PaymentDetails/:id",
          name: $t("PettycashPayment"),
          component: () =>
            import("@/views/financial-management/PettyCash/PaymentDetails.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount") },
              { title: $t("PettycashPayment") },
              { title: $t("PaymentInfo"), active: true },
            ],
            pageTitle: $t("PettycashPayment"),
            rule: "editor",
            permission: "GetAllFiles|AddFileAttachmentCommand |ChangeStateToConfrim",
          },
        },
        {
          path: "/Payment/CreateChargePettycashsAccount",
          name: $t("CreateChargePettycashsAccount"),
          component: () =>
            import("@/views/financial-management/PettyCash/CreateChargePettycashsAccount.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount") , url:"/PettyCash/ChargePettycashsAccount" },
              { title: $t("CreateChargePettycashsAccount"), active: true },
            ],
            pageTitle: $t("CreateChargePettycashsAccount"),
            rule: "editor",
            permission: "GetAllFundReplenishment|GetAllExpenseByFundReplenishmentId|CreateFundReplenishment|GetAllSubsidiaryAndDetailAccounts",
          },
        },
        {
          path: "/Payment/PettycashPayment/:id",
          name: $t("CreatePettycashPayment"),
          component: () =>
            import("@/views/financial-management/PettyCash/CreatePettycashPayment.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount") , url:"/PettyCash/ChargePettycashsAccount"},
              { title: $t("PettycashPayment") },

              { title: $t("CreatePettycashPayment"), active: true },
            ],
            pageTitle: $t("CreatePettycashPayment"),
            rule: "editor",
            permission: "CreateExpense|GetAllSubsidiaryAndDetailAccounts|GetAllExpense|GetAllExpenseByFundReplenishmentId|GetAllFiles|AddFileAttachmentCommand",
          },
        },
        {
          path: "/PettyCash/ChargePettycashsAccount",
          name: $t("ChargePettycashsAccount"),
          component: () =>
            import("@/views/financial-management/PettyCash/ChargePettyCashesAccount.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("مدیریت اعتبارات") },
              { title: $t("ChargePettycashsAccount"), active: true },
            ],
            pageTitle: $t("ChargePettycashsAccount"),
            rule: "editor",
            permission: "GetAllFundReplenishment|GetAllExpenseByFundReplenishmentId|CreateFundReplenishment|GetAllSubsidiaryAndDetailAccounts",
          },
        },
        ///contentManagement
        {
          path: "/Contents",
          name: "contents",
          component: () =>
            import("@/views/content-management/Contents/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement"), active: true },
              { title: $t("Contents"), active: true },
            ],
            pageTitle: $t("Contents"),
            rule: "editor",
            permission: "",
            // "CreateMenu|ModifyMenu|DeleteMenu|AssignProducts|UnAssignProducts"
          },
        },
        {
          path: "/contents/:id",
          name: "edit-content",
          component: () =>
            import(
              "@/views/content-management/Contents/ContentItems/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Contents"), url: "/contents" },
              { title: "", active: true },
            ],
            pageTitle: $t("EditContent"),
            rule: "editor",
          },
        },
        {
          path: "/Menus/Index",
          name: $t("Menus"),
          component: () => import("@/views/content-management/Menus/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Menus"), active: true },
            ],
            pageTitle: $t("Menus"),
            rule: "editor",
            permission:
              "CreateMenu|ModifyMenu|DeleteMenu|AssignProductsToMenu|UnAssignProductsFromMenu",
            // "CreateMenu|ModifyMenu|DeleteMenu|AssignProducts|UnAssignProducts"
          },
        },
        {
          path: "/Banners/Index",
          name: $t("Banners"),
          component: () =>
            import("@/views/content-management/Banners/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Banners"), active: true },
            ],
            pageTitle: $t("Banners"),
            rule: "editor",
            permission: "CreateBanner|ModifyBanner|DeleteBanner",
          },
        },
        {
          path: "/Banners/Create",
          name: $t("CreateBanner"),
          component: () =>
            import("@/views/content-management/Banners/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Banners"), url: "/Banners/Index" },
              { title: $t("CreateBanner"), active: true },
            ],
            pageTitle: $t("CreateBanner"),
            rule: "editor",
          },
        },
        {
          path: "/Banners/Modify/:id",
          name: $t("EditBanner"),
          component: () =>
            import("@/views/content-management/Banners/Modify.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Banners"), url: "/Banners/Index" },
              { title: $t("EditBanner"), active: true },
            ],
            pageTitle: $t("EditBanner"),
            rule: "editor",
          },
        },
        {
          path: "/ProductCollection/Index",
          name: $t("ProductCollection"),
          component: () =>
            import("@/views/content-management/ProductCollection/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("ProductCollection"), active: true },
            ],
            pageTitle: $t("ProductCollection"),
            rule: "editor",
            permission:
              // "CreateProductCollection|DeleteProductCollection|UnAssignProducts|AssignProducts|ModifyProductCollection"
              "CreateProductCollection|DeleteProductCollection|UnAssignProductsFromProductCollection|AssignProductsToProductCollection|ModifyProductCollection",
          },
        },
        {
          path: "/ProductCollection/Management/:id",
          name: $t("ProductCollectionManagement"),
          component: () =>
            import(
              "@/views/content-management/ProductCollection/Management.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              {
                title: $t("ProductCollection"),
                url: "/ProductCollection/Index",
              },
              { title: $t("ProductCollectionManagement"), active: true },
            ],
            pageTitle: $t("ProductCollectionManagement"),
            rule: "editor",
          },
        },
        {
          path: "/Pages/Index",
          name: $t("Pages"),
          component: () => import("@/views/content-management/Pages/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Pages"), active: true },
            ],
            pageTitle: $t("Pages"),
            rule: "editor",
            permission: "CreatePage|ModifyPage|DeletePage",
          },
        },
        {
          path: "/Pages/Create",
          name: $t("CreateNewPage"),
          component: () =>
            import("@/views/content-management/Pages/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Pages"), url: "/Pages/Index" },
              { title: $t("CreateNewPage"), active: true },
            ],
            pageTitle: $t("CreateNewPage"),
            rule: "editor",
          },
        },
        {
          path: "/Pages/Modify/:id",
          name: $t("EditPage"),
          component: () =>
            import("@/views/content-management/Pages/Modify.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Pages"), url: "/Pages/Index" },
              { title: $t("EditPage"), active: true },
            ],
            pageTitle: $t("EditPage"),
            rule: "editor",
          },
        },
        {
          path: "/Blog/Index",
          name: $t("Blog"),
          component: () =>
            import("@/views/content-management/Weblog/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), active: true },
            ],
            pageTitle: $t("Blog"),
            rule: "editor",
            permission:
              "CreatePost|ModifyPost|DeletePost|ModifyCmsCategory|DeleteCategory|CreateCategory|ModifyTag|DeleteTag|CreateTag",
          },
        },
        {
          path: "/Weblog/Create",
          name: $t("CreatePost"),
          component: () =>
            import("@/views/content-management/Weblog/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("CreatePost"), active: true },
            ],
            pageTitle: $t("CreatePost"),
            rule: "editor",
          },
        },
        {
          path: "/Weblog/Tags/Index",
          name: $t("Tags"),
          component: () =>
            import("@/views/content-management/Weblog/Tags/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("Tags"), active: true },
            ],
            pageTitle: $t("Tags"),
            rule: "editor",
          },
        },
        {
          path: "/Weblog/PostCategory/Index",
          name: $t("PostCategories"),
          component: () =>
            import("@/views/content-management/Weblog/PostCategory/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("PostCategories"), active: true },
            ],
            pageTitle: $t("PostCategories"),
            rule: "editor",
          },
        },
        {
          path: "/Weblog/PostCategory/Create",
          name: $t("CreateNewPostCategory"),
          component: () =>
            import("@/views/content-management/Weblog/PostCategory/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("PostCategories"), url: "/PostCategory/Index" },
              { title: $t("CreateNewPostCategory"), active: true },
            ],
            pageTitle: $t("CreateNewPostCategory"),
            rule: "editor",
          },
        },
        {
          path: "/Weblog/PostCategory/Modify/:id",
          name: $t("EditPostCategory"),
          component: () =>
            import("@/views/content-management/Weblog/PostCategory/Modify.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("PostCategories"), url: "/PostCategory/Index" },
              { title: $t("EditPostCategory"), active: true },
            ],
            pageTitle: $t("EditPostCategory"),
            rule: "editor",
          },
        },
        {
          path: "/weblog/Modify/:id",
          name: $t("EditPost"),
          component: () =>
            import("@/views/content-management/Weblog/Modify.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Blog"), url: "/Blog/Index" },
              { title: $t("EditPost"), active: true },
            ],
            pageTitle: $t("EditPost"),
            rule: "editor",
          },
        },
        {
          path: "/ProductCommnets/Index",
          name: $t("ProductCommnets"),
          component: () =>
            import("@/views/content-management/ProductComments/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("ProductCommnets"), active: true },
            ],
            pageTitle: $t("ProductCommnets"),
            rule: "editor",
            permission: "ModifyCmsCategory",
          },
        },
        {
          path: "/Complaints/Index",
          name: $t("Complaints"),
          component: () =>
            import("@/views/content-management/Complaints/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Complaints"), active: true },
            ],
            pageTitle: $t("Complaints"),
            rule: "editor",
            // permission: "CreateComplaint|ModifyComplaint",
            permission: "",

          },
        },
        {
          path: "/Forums/Index",
          name: $t("Forums"),
          component: () =>
            import("@/views/content-management/Forums/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("Forums"), active: true },
            ],
            pageTitle: $t("Forums"),
            rule: "editor",
            permission: "CreateForum|ModifyForum",
          },
        },
        {
          path: "/FAQ/Index",
          name: $t("FAQ"),
          component: () => import("@/views/content-management/FAQ/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("FAQ"), active: true },
            ],
            pageTitle: $t("FAQ"),
            rule: "editor",
            permission:
              "ModifyFaq|CreateFaq|DeleteFaq|CreateFaqCategory|ModifyFaqCategory|DeleteFaqCategory",
          },
        },
        {
          path: "/FAQ/FAQCategory/Index",
          name: $t("PostCategories"),
          component: () =>
            import("@/views/content-management/FAQ/FAQCategory/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("FAQ"), url: "/FAQ/Index" },
              { title: $t("FAQCategory"), active: true },
            ],
            pageTitle: $t("FAQCategory"),
            rule: "editor",
          },
        },
        {
          path: "/FAQ/Create",
          name: $t("CreatePost"),
          component: () => import("@/views/content-management/FAQ/Create.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("FAQ"), url: "/FAQ/Index" },
              { title: $t("CreateFAQ"), active: true },
            ],
            pageTitle: $t("CreateFAQ"),
            rule: "editor",
          },
        },
        {
          path: "/FAQ/Modify/:id",
          name: $t("EditFAQ"),
          component: () => import("@/views/content-management/FAQ/Modify.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("FAQ"), url: "/FAQ/Index" },
              { title: $t("EditFAQ"), active: true },
            ],
            pageTitle: $t("EditFAQ"),
            rule: "editor",
          },
        },
        {
          path: "/YaldaEvent/Index",
          name: $t("YaldaEvent"),
          component: () =>
            import("@/views/content-management/YaldaEvent/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("YaldaEvent"), active: true },
            ],
            pageTitle: $t("YaldaEvent"),
            rule: "editor",
            permission: "YaldaEvent",
          },
        },
        {
          path: "/YaldaEvent/Details/:id",
          name: $t("Details"),
          component: () =>
            import("@/views/content-management/YaldaEvent/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("YaldaEvent"), url: "/YaldaEvent/Index" },
              { title: $t("Details"), active: true },
            ],
            pageTitle: $t("Details"),
            rule: "editor",
          },
        },
        {
          path: "/RecruitmentRequest/Index",
          name: $t("RecruitmentRequest"),
          component: () =>
            import("@/views/content-management/RecruitmentRequest/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("RecruitmentRequest"), active: true },
            ],
            pageTitle: $t("RecruitmentRequest"),
            rule: "editor",
          },
        },
        {
          path: "/RecruitmentRequest/Details/:id",
          name: $t("Details"),
          component: () =>
            import("@/views/content-management/RecruitmentRequest/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              {
                title: $t("RecruitmentRequest"),
                url: "/RecruitmentRequest/Index",
              },
              { title: $t("Details"), active: true },
            ],
            pageTitle: $t("Details"),
            rule: "editor",
          },
        },
        {
          path: "/WebsiteVisitors/Index",
          name: $t("WebsiteVisitors"),
          component: () =>
            import("@/views/content-management/WebsiteVisitors/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("WebsiteVisitors"), active: true },
            ],
            pageTitle: $t("WebsiteVisitors"),
            rule: "editor",
            permission: "WebsiteVisitors",
          },
        },
        {
          path: "/CriticsAndSuggestions/Index",
          name: $t("CriticsAndSuggestions"),
          component: () =>
            import(
              "@/views/content-management/CriticsAndSuggestions/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ContentManagement") },
              { title: $t("CriticsAndSuggestions"), active: true },
            ],
            pageTitle: $t("CriticsAndSuggestions"),
            rule: "editor",
            permission:
              "AssignAnswer|GetAllSuggestions|GetAllSuggestionsWithOutAnswer|GetAllSuggestionWithAnswer",
          },
        },
        ///Tadbir
        {
          path: "/TadbirOrders/Index",
          name: $t("TadbirOrders"),
          component: () => import("@/views/tadbir/Orders/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialReports") },
              { title: $t("TadbirOrders"), active: true },
            ],
            pageTitle: $t("TadbirOrders"),
            rule: "editor",
          },
        },
        {
          path: "/TadbirOrders/Details/:id",
          name: $t("TadbirOrder"),
          component: () => import("@/views/tadbir/Orders/Details.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialReports") },
              { title: $t("TadbirOrders"), url: "/TadbirOrders/Index" },
              { title: $t("TadbirOrder"), active: true },
            ],
            pageTitle: $t("TadbirOrder"),
            rule: "editor",
          },
        },
        {
          path: "/TadbirCustomers/Index",
          name: $t("TadbirCustomers"),
          component: () => import("@/views/tadbir/Customers/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialReports") },
              { title: $t("TadbirCustomers"), active: true },
            ],
            pageTitle: $t("TadbirCustomers"),
            rule: "editor",
          },
        },

        {
          path: "/TadbirReceipts/Index",
          name: $t("TadbirReceipts"),
          component: () => import("@/views/tadbir/Receipts/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("FinancialReports") },
              { title: $t("TadbirReceipts"), active: true },
            ],
            pageTitle: $t("TadbirReceipts"),
            rule: "editor",
          },
        },
        {
          path: "/ManagementDashboard",
          name: $t("ManagementDashboard"),
          component: () => import("@/views/ManagementDashboard/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("ManagementDashboard"), active: true },
            ],
            pageTitle: $t("ManagementDashboard"),
            rule: "editor",
          },
        },
        {
          path: "/Profile/Index",
          name: $t("Profile"),
          component: () => import("@/views/profile/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Profile") },
              { title: $t("Profile"), active: true },
            ],
            pageTitle: $t("Profile"),
            rule: "editor",
          },
        },
        ///NotificationManagement
        {
          path: "/NotificationTemplate/Index",
          name: $t("NotificationTemplate"),
          component: () =>
            import(
              "@/views/notification-management/NotificationTemplate/Index.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("NotificationManagement") },
              { title: $t("NotificationTemplate"), active: true },
            ],
            pageTitle: $t("NotificationTemplate"),
            rule: "editor",
            permission:
              "ModifyNotificationTemplate|GetAllNotificationTemplates|GetAllReceiverSettings",
          },
        },
        {
          path: "/Receivers/Index",
          name: $t("Receivers"),
          component: () =>
            import("@/views/notification-management/Receivers/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("NotificationManagement") },
              { title: $t("Receivers"), active: true },
            ],
            pageTitle: $t("Receivers"),
            rule: "editor",
            permission: "CreateReceiver|GetAllReceiversByName",
          },
        },
        {
          path: "/Notifications/Index",
          name: $t("Notifications"),
          component: () =>
            import("@/views/notification-management/Notifications/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("NotificationManagement") },
              { title: $t("Notifications"), active: true },
            ],
            pageTitle: $t("Notifications"),
            rule: "editor",
            permission: "",
          },
        },
         ///Geography
         {
          path: "/Countries/Index",
          name: $t("Countries"),
          component: () =>
            import("@/views/Geography/Countries/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Geography") },
             
              { title: $t("Countries"), active: true },
            ],
            pageTitle: $t("Countries"),
            rule: "editor",
          },
        },
        {
          path: "/Provinces/Index",
          name: $t("Provinces"),
          component: () =>
            import("@/views/Geography/Provinces/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Geography") },

              { title: $t("Provinces"), active: true },
            ],
            pageTitle: $t("Provinces"),
            rule: "editor",
          },
        },
        {
          path: "/Cities/Index",
          name: $t("Cities"),
          component: () =>
            import("@/views/Geography/Cities/Index.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: $t("Geography") },

              { title: $t("Cities"), active: true },
            ],
            pageTitle: $t("Cities"),
            rule: "editor",
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/callback",
          name: "authCallback",
          component: () => import("@/views/Callback.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/unauthorized",
          name: "authUnauthorized",
          component: () => import("@/views/Unauthorized.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/forgot-password",
          name: "pageForgotPassword",
          component: () => import("@/views/pages/ForgotPassword.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/reset-password",
          name: "pageResetPassword",
          component: () => import("@/views/pages/ResetPassword.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/lock-screen",
          name: "pageLockScreen",
          component: () => import("@/views/pages/LockScreen.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/comingsoon",
          name: "pageComingSoon",
          component: () => import("@/views/pages/ComingSoon.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/error-404",
          name: "pageError404",
          component: () => import("@/views/pages/Error404.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/error-500",
          name: "pageError500",
          component: () => import("@/views/pages/Error500.vue"),
          meta: {
            rule: "editor",
          },
        },
        {
          path: "/pages/maintenance",
          name: "pageMaintenance",
          component: () => import("@/views/pages/Maintenance.vue"),
          meta: {
            rule: "editor",
          },
        },
        
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/pages/error-404",
    },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});
router.beforeEach(async (to, from, next) => {
  if (to.query.breadcrumbName) {
    to.meta.breadcrumb[to.meta.breadcrumb.length - 1].title =
      to.query.breadcrumbName;
  }
  if (to.query.breadcrumbNameManual) {
    if (to.meta.breadcrumb[1].title == "مدیریت طرفهای تجاری") {
      to.meta.breadcrumb[3].title =
        "شعبه های " + to.query.breadcrumbNameManual.persianTitleParent;
    } else if (to.meta.breadcrumb[1].title == "مدیریت انبار") {
      to.meta.breadcrumb[3].title =
        "لیست شمارش‌های " + to.query.breadcrumbNameManual.persianTitleParent;

      if (to.meta.breadcrumb.length == 5) {
        to.meta.breadcrumb[3].url +=
          to.query.breadcrumbNameManual.persianTitleChild;
        to.meta.breadcrumb[4].title =
          "جزییات شمارش‌ " + to.query.breadcrumbNameManual.persianTitleChild;
      }
    } else if (to.meta.breadcrumb[1].title == "مدیریت حمل و نقل") {
      to.meta.breadcrumb[to.meta.breadcrumb.length - 2].title =
        to.query.breadcrumbNameManual.persianTitle;
      to.meta.breadcrumb[to.meta.breadcrumb.length - 2].url =
        "/" + to.query.breadcrumbNameManual.Title + "/Index";
      to.meta.breadcrumb[to.meta.breadcrumb.length - 1].title = "جزییات";
    }
    else if  (to.meta.breadcrumb[1].title == "مدیریت مالی") {
      to.meta.breadcrumb[3].title =
        "عنوان های هزینه " + to.query.breadcrumbNameManual.persianTitleParent;
  }
    else if  (to.meta.breadcrumb[1].title == "مدیریت اعتبارات" && to.meta.breadcrumb[2].title === "آرتیکل ها") {
      to.meta.breadcrumb[2].title =
        "آرتیکل های " + to.query.breadcrumbNameManual.persianTitleParent;
  }
    else if  (to.meta.breadcrumb[1].title == "مدیریت اعتبارات" && to.meta.breadcrumb[2].title === "مراکز هزینه") {
      to.meta.breadcrumb[3].title =
        "حساب های مرتبط با " + to.query.breadcrumbNameManual.persianTitleParent;
  }
  }
  if (
    to.path === "/pages/forgot-password" ||
    to.path === "/pages/error-404" ||
    to.path === "/pages/error-500" ||
    to.path === "/pages/register" ||
    to.path === "/callback" ||
    to.path === "/unauthorized" ||
    to.path === "/pages/comingsoon"
  ) {
    return next();
  } else {
    if (await AuthService.getSignedIn()) {
      if (to.meta.permission !== undefined) {
        if (to.meta.permission === "") {
          return next();
        }
        if (await SecurityService.hasPermission(to.meta.permission)) {
          return next();
        } else {
          return next("/unauthorized");
        }
      }
      return next();
    }
  }
});

router.beforeEach((to, from, next) => {
  if (from.name) {
    // Check if 'from' route is defined (not on initial load)
    Vue.prototype.$lastRoute = from.fullPath;
  }
  next();
});

export default router;
